import { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { 
  Shield, 
  Building2, 
  Clock,
  RefreshCw,
  Trash2,
  Key,
  CreditCard,
  XCircle,
  CheckCircle,
  Snowflake,
  Unlock,
  Activity,
  Search,
  AlertTriangle,
  Filter,
  Eye,
  X,
  Inbox,
  Bug,
  Monitor,
  ChevronRight,
  ChevronDown,
  Mail,
  Phone as PhoneIcon,
  Zap,
  LogIn,
  Star,
  BarChart3,
  Calendar,
  Megaphone,
  Loader2,
  Globe,
  ExternalLink,
  Receipt,
  Gift,
  DollarSign,
  TrendingUp,
  AlertCircle,
  Handshake,
  Bell,
  Send,
  Pause,
  Play,
  Route,
} from 'lucide-react';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title as ChartTitle,
  Tooltip,
  Legend,
  Filler,
} from 'chart.js';
import { Line } from 'react-chartjs-2';
import { useSuperAdmin } from '../hooks/useSuperAdmin';
import {
  SuperAdminService,
  BusinessSummary,
  type SuperAdminBusinessListStatus,
  BusinessUser,
  RemoteSession,
  StripeInvoiceMirrorRow,
  StripeSubscriptionMirrorRow,
  type BusinessStripeSubscriptionSummary,
  type SubscriptionPlanOption,
  type StripeBillingMetrics,
  type AiTokenPurchaseRow,
  type StripeInvoiceAdminAction,
  type SocialSeatUsage,
} from '../services/superAdminService';
import GrantBusinessAccessModal, { type GrantAccessMode } from '../components/GrantBusinessAccessModal';
import StripeInvoiceActionModal, { stripeInvoiceAvailableActions } from '../components/StripeInvoiceActionModal';
import ReferralPartnersPanel from '../components/ReferralPartnersPanel';
import SignupAnalyticsPanel from '../components/admin/SignupAnalyticsPanel';
import SignupFlowReportsPanel from '../components/admin/SignupFlowReportsPanel';
import SuperAdminServiceHoursPanel from '../components/super-admin/SuperAdminServiceHoursPanel';
import { useImageViewer } from '../components/ImageViewerProvider';
import {
  convertCentsToCadCents,
  convertDollarsToCad,
  formatCad,
  formatCadCents,
  getExchangeRatesToCad,
  type ExchangeRatesToCad,
} from '../lib/currencyToCad';

const DEFAULT_CAD_RATES: ExchangeRatesToCad = { cad: 1, usd: 1.37 };

function superAdminBusinessBadgeLabel(
  status: SuperAdminBusinessListStatus,
  adminAccess?: BusinessSummary['admin_access'],
): string {
  if (adminAccess?.kind === 'trial') return 'Trial';
  switch (status) {
    case 'past_due':
      return 'Past due';
    case 'canceled':
      return 'Canceled';
    case 'inactive':
      return 'Inactive';
    case 'suspended':
      return 'Suspended';
    default:
      return 'Active';
  }
}

function formatPlatformPlanPrice(price: number | undefined): string | null {
  if (price == null) return null;
  const formatted = new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: price % 1 === 0 ? 0 : 2,
    maximumFractionDigits: 2,
  }).format(price);
  return `${formatted}/month`;
}

function adminAccessTimingLine(adminAccess: NonNullable<BusinessSummary['admin_access']>): string | null {
  if (adminAccess.kind === 'trial' && adminAccess.trial_end_date) {
    return `Trial ends ${new Date(adminAccess.trial_end_date).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    })}`;
  }
  if (adminAccess.kind === 'complimentary') return 'Admin granted · No expiry';
  return 'Admin granted';
}

function formatMirrorInvoiceAmountCad(
  cents: number,
  currency: string | null | undefined,
  rates: ExchangeRatesToCad,
): string {
  return formatCadCents(convertCentsToCadCents(cents, currency, rates));
}

function InvoiceMetricCard({
  label,
  value,
  sub,
  icon: Icon,
  accentClass,
}: {
  label: string;
  value: string;
  sub?: string;
  icon: typeof DollarSign;
  accentClass: string;
}) {
  return (
    <div
      className="bg-white rounded-[14px] p-3.5 min-w-[148px] flex-1"
      style={{ boxShadow: '0 0 0 0.5px rgba(0,0,0,0.04), 0 1px 2px rgba(0,0,0,0.04)' }}
    >
      <div className="flex items-start justify-between gap-2 mb-2">
        <p className="text-[11px] font-semibold text-gray-500 uppercase tracking-wide leading-tight">{label}</p>
        <div className={`h-7 w-7 rounded-full flex items-center justify-center flex-shrink-0 ${accentClass}`}>
          <Icon className="h-3.5 w-3.5" />
        </div>
      </div>
      <p className="text-[20px] font-bold text-gray-900 tabular-nums leading-none">{value}</p>
      {sub && <p className="text-[11px] text-gray-400 mt-1.5 leading-snug">{sub}</p>}
    </div>
  );
}

function stripeInvoiceStatusClass(status: string): string {
  switch (status) {
    case 'paid':
      return 'bg-green-100 text-green-700';
    case 'open':
      return 'bg-amber-100 text-amber-700';
    case 'void':
      return 'bg-gray-100 text-gray-600';
    case 'uncollectible':
      return 'bg-red-100 text-red-700';
    default:
      return 'bg-gray-100 text-gray-600';
  }
}

function stripeSubscriptionKindLabel(kind: string): string {
  switch (kind) {
    case 'platform':
      return 'Platform plan';
    case 'hosting':
      return 'Website hosting';
    case 'customer_service':
      return 'Customer subscription';
    default:
      return 'Subscription';
  }
}

function stripeSubscriptionStatusClass(status: string): string {
  switch (status) {
    case 'active':
    case 'trialing':
      return 'bg-green-100 text-green-700';
    case 'past_due':
      return 'bg-amber-100 text-amber-700';
    case 'canceled':
      return 'bg-gray-100 text-gray-600';
    default:
      return 'bg-red-100 text-red-700';
  }
}

function formatShortStripeDate(iso: string): string {
  return new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

function stripeSubTimingLines(
  sub: BusinessStripeSubscriptionSummary | StripeSubscriptionMirrorRow,
): string[] {
  const lines: string[] = [];
  const isPastDue = sub.status === 'past_due' || sub.status === 'unpaid';

  if (isPastDue && sub.next_payment_attempt) {
    lines.push(`Next retry ${formatShortStripeDate(sub.next_payment_attempt)}`);
  }
  if (sub.cancel_at) {
    lines.push(`Cancels ${formatShortStripeDate(sub.cancel_at)}`);
  } else if (!isPastDue && sub.current_period_end) {
    lines.push(`Renews ${formatShortStripeDate(sub.current_period_end)}`);
  }

  return lines;
}

import { DeleteBusinessModal } from '../components/DeleteBusinessModal';
import { BusinessUserList } from '../components/super-admin/BusinessUserList';
import BlogManagerModal from '../components/super-admin/BlogManagerModal';
import { RemoteAccessViewer } from '../components/super-admin/RemoteAccessViewer';
import { ImpersonationService } from '../services/impersonationService';
import { supabase } from '../lib/supabaseClient';
import { connectWebsite, disconnectWebsite, getRepoConnection, listGithubRepos, type RepoConnectionStatus } from '../lib/siteFactory';
import {
  createNexusHostingCheckout,
  describeCardCharge,
  isNexusHostingActive,
} from '../lib/nexusHostingSubscription';
import { handleStripeCheckoutSuccess } from '../lib/stripeCheckout';
import PortalPanel from '../components/PortalPanel';
import BillingByDayCard from '../components/super-admin/BillingByDayCard';

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, ChartTitle, Tooltip, Legend, Filler);

// Helper function to format date as "Aug 1st, 2024"
const formatJoinedDate = (dateString: string): string => {
  const date = new Date(dateString);
  const month = date.toLocaleDateString('en-US', { month: 'short' });
  const day = date.getDate();
  const year = date.getFullYear();
  
  // Add ordinal suffix to day
  const getOrdinalSuffix = (day: number): string => {
    if (day > 3 && day < 21) return 'th';
    switch (day % 10) {
      case 1: return 'st';
      case 2: return 'nd';
      case 3: return 'rd';
      default: return 'th';
    }
  };
  
  return `${month} ${day}${getOrdinalSuffix(day)}, ${year}`;
};

function nexusHostingLabel(status: string | null | undefined): string {
  const s = (status || '').toLowerCase();
  if (s === 'active' || s === 'trialing') return s === 'trialing' ? 'Trialing' : 'Active';
  if (s === 'past_due' || s === 'unpaid') return 'Past due';
  if (s === 'canceled' || s === 'cancelled') return 'Canceled';
  return 'Inactive';
}

function SuperAdminNexusWebsiteRow({
  business,
  onUpdated,
}: {
  business: BusinessSummary;
  onUpdated: (businessId: string, patch: Partial<BusinessSummary>) => void;
}) {
  const [value, setValue] = useState(business.nexus_website_url || '');
  const [saving, setSaving] = useState(false);
  const [activatingHosting, setActivatingHosting] = useState(false);
  const [openingHostingPortal, setOpeningHostingPortal] = useState(false);
  const [vercelProjectValue, setVercelProjectValue] = useState(business.vercel_project_id || '');
  const [savedVercelProjectId, setSavedVercelProjectId] = useState(business.vercel_project_id || '');
  const [savingVercel, setSavingVercel] = useState(false);
  const [sitePaused, setSitePaused] = useState<boolean | null>(business.vercel_hosting_paused ?? null);
  const [togglingSite, setTogglingSite] = useState(false);
  const [sendingLink, setSendingLink] = useState(false);
  const [blogOpen, setBlogOpen] = useState(false);
  const [githubRepo, setGithubRepo] = useState('');
  const [repoOptions, setRepoOptions] = useState<string[]>([]);
  const [repoListLoading, setRepoListLoading] = useState(true);
  const [repoListError, setRepoListError] = useState<string | null>(null);
  const [connectedRepo, setConnectedRepo] = useState<string | null>(null);
  const [repoStatus, setRepoStatus] = useState<RepoConnectionStatus | null>(null);
  const [checkingRepo, setCheckingRepo] = useState(false);
  const [connectingRepo, setConnectingRepo] = useState(false);

  useEffect(() => {
    setValue(business.nexus_website_url || '');
    setVercelProjectValue(business.vercel_project_id || '');
    setSavedVercelProjectId(business.vercel_project_id || '');
    setSitePaused(business.vercel_hosting_paused ?? null);
  }, [business.id, business.nexus_website_url, business.vercel_project_id, business.vercel_hosting_paused]);

  useEffect(() => {
    let active = true;
    setRepoListLoading(true);
    listGithubRepos()
      .then((repos) => {
        if (!active) return;
        setRepoOptions(repos);
        setRepoListError(null);
      })
      .catch((error: unknown) => {
        if (!active) return;
        setRepoOptions([]);
        setRepoListError(error instanceof Error ? error.message : 'Could not load repositories.');
      })
      .finally(() => { if (active) setRepoListLoading(false); });
    return () => { active = false; };
  }, [business.id]);

  useEffect(() => {
    let active = true;
    setCheckingRepo(true);
    getRepoConnection(business.id)
      .then((status) => {
        if (!active) return;
        setRepoStatus(status);
        setConnectedRepo(status.repo);
        if (status.repo) setGithubRepo((current) => (current.trim() ? current : status.repo as string));
      })
      .catch((error: unknown) => {
        if (!active) return;
        setRepoStatus({
          state: 'broken',
          repo: null,
          branch: null,
          message: error instanceof Error ? error.message : 'Not working. The connection could not be checked.',
        });
      })
      .finally(() => { if (active) setCheckingRepo(false); });
    return () => { active = false; };
  }, [business.id]);

  const handleSave = async () => {
    setSaving(true);
    try {
      const result = await SuperAdminService.updateNexusWebsiteUrl(business.id, value);
      if (!result.success) {
        alert(result.message);
        return;
      }
      const trimmed = value.trim();
      onUpdated(business.id, { nexus_website_url: trimmed === '' ? null : trimmed });
    } finally {
      setSaving(false);
    }
  };

  const handleClear = async () => {
    if (!business.nexus_website_url && !value.trim()) return;
    setSaving(true);
    try {
      const result = await SuperAdminService.updateNexusWebsiteUrl(business.id, null);
      if (!result.success) {
        alert(result.message);
        return;
      }
      setValue('');
      onUpdated(business.id, { nexus_website_url: null });
    } finally {
      setSaving(false);
    }
  };

  const hasSavedUrl = Boolean((business.nexus_website_url || '').trim());
  const hostLive = isNexusHostingActive(business.nexus_hosting_subscription_status);
  const hostStatusLower = (business.nexus_hosting_subscription_status || '').toLowerCase();
  const needsHostingBilling = ['past_due', 'unpaid'].includes(hostStatusLower);
  const baseAdminUrl = `${window.location.origin}/super-admin`;

  const handleActivateHosting = async (confirmCardOnFile = false) => {
    setActivatingHosting(true);
    try {
      const { data, error } = await createNexusHostingCheckout({
        businessId: business.id,
        successUrl: `${baseAdminUrl}?nexus_hosting=activated&session_id={CHECKOUT_SESSION_ID}`,
        cancelUrl: baseAdminUrl,
        confirmCardOnFile,
      });
      if (error) {
        alert(error.message || 'Could not start hosting checkout');
        return;
      }
      const d = data as {
        success?: boolean;
        url?: string;
        code?: string;
        message?: string;
        activated?: boolean;
        requiresConfirmation?: boolean;
        card?: { brand?: string; last4?: string };
        amount?: number;
        currency?: string;
        interval?: string;
      };
      if (d?.code === 'ALREADY_ACTIVE') {
        alert(d.message || 'Website hosting is already on an active subscription.');
        return;
      }
      if (d?.code === 'NEEDS_BILLING_PORTAL') {
        alert(
          d.message ||
            'Hosting subscription needs payment. Open billing portal to update the card.',
        );
        return;
      }
      // A card on file exists — confirm before charging it.
      if (d?.success && d.requiresConfirmation) {
        const ok = window.confirm(
          `Activate hosting for ${business.name}?\n\nThe customer's ${describeCardCharge(d)}`,
        );
        if (ok) await handleActivateHosting(true);
        return;
      }
      // Card on file was charged directly — no checkout redirect needed.
      if (d?.success && d.activated) {
        alert('Hosting activated — the card on file was charged.');
        onUpdated(business.id, { nexus_hosting_subscription_status: 'active' });
        return;
      }
      if (d?.success && d.url) {
        window.location.href = d.url;
        return;
      }
      alert(d?.message || 'Could not start hosting checkout');
    } finally {
      setActivatingHosting(false);
    }
  };

  const handleHostingBillingPortal = async () => {
    setOpeningHostingPortal(true);
    try {
      const { data, error } = await supabase.functions.invoke('stripe-subscription', {
        body: {
          action: 'create-billing-portal',
          businessId: business.id,
          returnUrl: window.location.href,
        },
      });
      if (error || !data?.url) {
        alert('Unable to open billing portal');
        return;
      }
      window.location.href = data.url as string;
    } finally {
      setOpeningHostingPortal(false);
    }
  };

  const handleSaveVercelProject = async () => {
    setSavingVercel(true);
    try {
      const result = await SuperAdminService.linkVercelProject(business.id, vercelProjectValue);
      if (!result.success) {
        alert(result.message);
        return;
      }
      const trimmed = vercelProjectValue.trim();
      setSavedVercelProjectId(trimmed);
      if (trimmed === '') setSitePaused(null);
      // Report both fields so the parent's business state stays in sync —
      // otherwise the prop-sync effect resets this row from stale data.
      const patch: Partial<BusinessSummary> = { vercel_project_id: trimmed === '' ? null : trimmed };
      // Linking pulls the site URL from the project's domains automatically.
      if (result.websiteUrl) {
        setValue(result.websiteUrl);
        patch.nexus_website_url = result.websiteUrl;
      }
      // Linking also runs an immediate enforcement sync — reflect the outcome.
      if (trimmed !== '' && typeof result.paused === 'boolean') {
        setSitePaused(result.paused);
        patch.vercel_hosting_paused = result.paused;
      }
      if (trimmed !== '') alert(result.message);
      onUpdated(business.id, patch);
    } finally {
      setSavingVercel(false);
    }
  };

  const handleConnectRepo = async () => {
    const projectId = vercelProjectValue.trim() || savedVercelProjectId || '';
    if (!githubRepo.trim()) return;
    setConnectingRepo(true);
    try {
      const site = await connectWebsite(business.id, githubRepo.trim(), projectId);
      setConnectedRepo(site.github_repo);
      setGithubRepo(site.github_repo || site.connection?.repo || githubRepo.trim());
      if (site.connection) setRepoStatus(site.connection);
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Could not connect the repository.';
      setRepoStatus({
        state: 'broken',
        repo: githubRepo.trim(),
        branch: null,
        message: message.startsWith('Not working') ? message : `Not working. ${message}`,
      });
    } finally {
      setConnectingRepo(false);
    }
  };

  const handleDisconnectRepo = async () => {
    if (!connectedRepo) return;
    if (!window.confirm(`Disconnect ${connectedRepo}? The web developer bot is retired. The Vercel hosting link stays.`)) return;
    setConnectingRepo(true);
    try {
      await disconnectWebsite(business.id);
      setConnectedRepo(null);
      setGithubRepo('');
      setRepoStatus({ state: 'none', repo: null, branch: null, message: '' });
    } catch (error) {
      alert(error instanceof Error ? error.message : 'Could not disconnect the repository.');
    } finally {
      setConnectingRepo(false);
    }
  };

  const handleSendActivationLink = async () => {
    setSendingLink(true);
    try {
      const result = await SuperAdminService.sendHostingActivationLink(business.id);
      alert(result.message);
    } finally {
      setSendingLink(false);
    }
  };

  const handleToggleSite = async (action: 'pause' | 'unpause') => {
    setTogglingSite(true);
    try {
      const result = await SuperAdminService.setVercelHostingState(business.id, action);
      if (!result.success) {
        alert(result.message);
        return;
      }
      if (!result.skipped) {
        setSitePaused(result.paused === true);
        onUpdated(business.id, { vercel_hosting_paused: result.paused === true });
      }
    } finally {
      setTogglingSite(false);
    }
  };

  return (
    <>
      <div className="border-t border-gray-200/40" />
      <div className="px-3 py-3 space-y-2">
        <div className="flex items-center gap-2">
          <Globe className="h-3.5 w-3.5 text-gray-400" aria-hidden />
          <span className="text-[13px] font-semibold text-gray-500 uppercase tracking-wide">Nexus website</span>
        </div>
        <div className="flex items-center gap-2">
          <input
            type="text"
            placeholder="Vercel project ID (prj_...)"
            value={vercelProjectValue}
            onChange={(e) => setVercelProjectValue(e.target.value)}
            disabled={savingVercel}
            className="flex-1 min-w-0 text-[13px] rounded-lg border border-gray-200 px-2.5 py-2 text-gray-900 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-400 disabled:opacity-60 font-mono"
          />
          <button
            type="button"
            onClick={handleSaveVercelProject}
            disabled={savingVercel || (vercelProjectValue.trim() === '' && !savedVercelProjectId)}
            className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-[12px] font-semibold text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-40 shrink-0"
          >
            {savingVercel ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : null}
            {vercelProjectValue.trim() && vercelProjectValue.trim() === savedVercelProjectId ? 'Re-link' : 'Link'}
          </button>
        </div>
        <p className="text-[11px] text-gray-400 leading-snug">
          Linking pulls the site URL from the project's domains automatically. Or set the URL manually below.
        </p>
        <div className="flex items-center gap-2">
          <select
            value={githubRepo}
            onChange={(e) => setGithubRepo(e.target.value)}
            disabled={connectingRepo || repoListLoading}
            aria-label="GitHub repository"
            className="flex-1 min-w-0 text-[13px] rounded-lg border border-gray-200 px-2.5 py-2 text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-400 disabled:opacity-60 font-mono bg-white"
          >
            <option value="">
              {repoListLoading ? 'Loading repositories…' : repoOptions.length === 0 ? 'No repositories available' : 'Select a repository'}
            </option>
            {githubRepo && !repoOptions.includes(githubRepo) ? (
              <option value={githubRepo}>{githubRepo}</option>
            ) : null}
            {repoOptions.map((repo) => (
              <option key={repo} value={repo}>{repo}</option>
            ))}
          </select>
          <button
            type="button"
            onClick={handleConnectRepo}
            disabled={connectingRepo || !githubRepo.trim()}
            className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-[12px] font-semibold text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-40 shrink-0"
          >
            {connectingRepo ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : null}
            Connect
          </button>
        </div>
        {repoListError ? (
          <p className="text-[11px] leading-snug text-rose-600">{repoListError}</p>
        ) : null}
        {checkingRepo ? (
          <p className="text-[11px] text-gray-400 leading-snug">Checking GitHub connection…</p>
        ) : repoStatus?.state === 'working' ? (
          <p className="text-[11px] leading-snug text-emerald-700">
            <span className="mr-1.5 inline-block h-1.5 w-1.5 rounded-full bg-emerald-500 align-middle" aria-hidden />
            {repoStatus.message}
          </p>
        ) : repoStatus?.state === 'broken' ? (
          <p className="text-[11px] leading-snug text-rose-600">
            <span className="mr-1.5 inline-block h-1.5 w-1.5 rounded-full bg-rose-500 align-middle" aria-hidden />
            {repoStatus.message}
          </p>
        ) : (
          <p className="text-[11px] text-gray-400 leading-snug">Connect the GitHub repository to add the web manager. Vercel is optional, and nothing is generated.</p>
        )}
        {connectedRepo ? (
          <button
            type="button"
            onClick={handleDisconnectRepo}
            disabled={connectingRepo}
            className="text-[11px] font-semibold text-rose-600 hover:underline disabled:opacity-40"
          >
            Disconnect
          </button>
        ) : null}
        <input
          type="url"
          inputMode="url"
          placeholder="https://customer-site.vercel.app"
          value={value}
          onChange={(e) => setValue(e.target.value)}
          disabled={saving}
          className="w-full text-[13px] rounded-lg border border-gray-200 px-2.5 py-2 text-gray-900 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-400 disabled:opacity-60"
        />
        <div className="flex flex-wrap gap-2">
          <button
            type="button"
            onClick={handleSave}
            disabled={saving}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[12px] font-semibold text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50"
          >
            {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : null}
            Save URL
          </button>
          <button
            type="button"
            onClick={handleClear}
            disabled={saving || (!business.nexus_website_url && !value.trim())}
            className="inline-flex items-center px-3 py-1.5 rounded-lg text-[12px] font-semibold text-gray-700 bg-gray-100 hover:bg-gray-200 disabled:opacity-40"
          >
            Clear
          </button>
          {business.nexus_website_url ? (
            <a
              href={/^https?:\/\//i.test(business.nexus_website_url) ? business.nexus_website_url : `https://${business.nexus_website_url}`}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg text-[12px] font-medium text-blue-600 hover:bg-blue-50"
            >
              Open <ExternalLink className="h-3 w-3" />
            </a>
          ) : null}
          {hasSavedUrl ? (
            <button
              type="button"
              onClick={() => setBlogOpen(true)}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[12px] font-semibold text-purple-700 bg-purple-50 hover:bg-purple-100 border border-purple-200"
            >
              Blog
            </button>
          ) : null}
        </div>
        {blogOpen ? (
          <BlogManagerModal businessId={business.id} businessName={business.name} onClose={() => setBlogOpen(false)} />
        ) : null}
        {hasSavedUrl ? (
          <div className="flex flex-col gap-2 rounded-lg border border-gray-200/80 bg-gray-50/80 px-2.5 py-2">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <div className="flex items-center gap-2 min-w-0">
                <span className="text-[11px] font-semibold text-gray-500 uppercase tracking-wide shrink-0">
                  Hosting
                </span>
                <span
                  className={`inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-semibold border shrink-0 ${
                    hostLive
                      ? 'bg-green-50 text-green-800 border-green-200'
                      : needsHostingBilling
                        ? 'bg-amber-50 text-amber-900 border-amber-200'
                        : 'bg-gray-100 text-gray-600 border-gray-200'
                  }`}
                >
                  {nexusHostingLabel(business.nexus_hosting_subscription_status)}
                </span>
              </div>
              <div className="flex flex-wrap gap-2">
                {!hostLive && !needsHostingBilling ? (
                  <>
                    <button
                      type="button"
                      onClick={handleSendActivationLink}
                      disabled={sendingLink || saving}
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[12px] font-semibold text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50"
                    >
                      {sendingLink ? (
                        <Loader2 className="h-3.5 w-3.5 animate-spin" />
                      ) : (
                        <Send className="h-3 w-3" />
                      )}
                      Send link
                    </button>
                    <button
                      type="button"
                      onClick={() => handleActivateHosting()}
                      disabled={activatingHosting || saving}
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[12px] font-semibold text-white bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50"
                    >
                      {activatingHosting ? (
                        <Loader2 className="h-3.5 w-3.5 animate-spin" />
                      ) : null}
                      Activate
                    </button>
                  </>
                ) : null}
                {needsHostingBilling ? (
                  <button
                    type="button"
                    onClick={handleHostingBillingPortal}
                    disabled={openingHostingPortal || saving}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[12px] font-semibold text-white bg-amber-600 hover:bg-amber-700 disabled:opacity-50"
                  >
                    {openingHostingPortal ? (
                      <Loader2 className="h-3.5 w-3.5 animate-spin" />
                    ) : null}
                    Billing portal
                  </button>
                ) : null}
              </div>
            </div>
            {savedVercelProjectId ? (
              <div className="flex flex-wrap items-center justify-between gap-2 border-t border-gray-200/60 pt-2">
                <div className="flex items-center gap-2 min-w-0">
                  <span className="text-[11px] font-semibold text-gray-500 uppercase tracking-wide shrink-0">
                    Site
                  </span>
                  <span
                    className={`inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-semibold border shrink-0 ${
                      sitePaused === false
                        ? 'bg-green-50 text-green-800 border-green-200'
                        : sitePaused === true
                          ? 'bg-red-50 text-red-800 border-red-200'
                          : 'bg-gray-100 text-gray-600 border-gray-200'
                    }`}
                  >
                    {sitePaused === false ? 'Live' : sitePaused === true ? 'Paused' : 'Not synced'}
                  </span>
                </div>
                <div className="flex flex-wrap gap-2">
                  {sitePaused !== true ? (
                    <button
                      type="button"
                      onClick={() => handleToggleSite('pause')}
                      disabled={togglingSite}
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[12px] font-semibold text-red-700 bg-red-50 hover:bg-red-100 border border-red-200 disabled:opacity-50"
                    >
                      {togglingSite ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Pause className="h-3 w-3" />}
                      Pause site
                    </button>
                  ) : (
                    <button
                      type="button"
                      onClick={() => handleToggleSite('unpause')}
                      disabled={togglingSite}
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[12px] font-semibold text-white bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50"
                    >
                      {togglingSite ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Play className="h-3 w-3" />}
                      Resume site
                    </button>
                  )}
                </div>
              </div>
            ) : null}
            <p className="text-[11px] text-gray-500 leading-snug">
              {savedVercelProjectId
                ? 'Hosting is billed separately from the main Nexus plan. The linked Vercel site is paused/resumed automatically from the subscription status.'
                : 'Hosting is billed separately from the main Nexus plan. Link the Vercel project ID to automatically pause the site when hosting lapses.'}
            </p>
          </div>
        ) : null}
      </div>
    </>
  );
}

function SuperAdminDashboard() {
  const { isSuperAdmin, loading: authLoading, user } = useSuperAdmin();
  const [businesses, setBusinesses] = useState<BusinessSummary[]>([]);
  const [loading, setLoading] = useState(true);
  
  // Filter state
  const [statusFilter, setStatusFilter] = useState<'active' | 'trialing' | 'suspended' | 'all'>('active');
  
  // Tab state
  const [activeTab, setActiveTab] = useState<'dashboard' | 'businesses' | 'invoices' | 'partners' | 'signups' | 'signup-flows' | 'activity' | 'errors' | 'requests' | 'bugs' | 'push'>('businesses');
  const activeTabRef = useRef(activeTab);
  activeTabRef.current = activeTab;
  
  // Activity log state
  const [activityLogs, setActivityLogs] = useState<any[]>([]);
  const [activityLoading, setActivityLoading] = useState(false);
  const [activityFilter, setActivityFilter] = useState<{
    activityType?: string;
    activityCategory?: string;
    userId?: string;
    businessId?: string;
    dateFrom?: string;
    dateTo?: string;
  }>({});
  const [activitySearch, setActivitySearch] = useState('');
  
  // Error logs state
  const [errorLogs, setErrorLogs] = useState<any[]>([]);
  const [errorLogsLoading, setErrorLogsLoading] = useState(false);
  const [pushLogs, setPushLogs] = useState<any[]>([]);
  const [pushLogsLoading, setPushLogsLoading] = useState(false);
  const [pushSearchTerm, setPushSearchTerm] = useState('');
  const [pushStatusFilter, setPushStatusFilter] = useState<'all' | 'success' | 'failed'>('all');
  // One-time Twilio inbound backfill (isolated test)
  const [smsBackfillLoading, setSmsBackfillLoading] = useState(false);
  const [smsBackfillResult, setSmsBackfillResult] = useState<string | null>(null);
  const runSmsBackfillTest = async () => {
    setSmsBackfillLoading(true);
    setSmsBackfillResult(null);
    try {
      const { data, error } = await supabase.functions.invoke('backfill-twilio-messages', {
        body: {
          startDate: '2026-06-07',
          endDate: '2026-06-10',
        },
      });
      if (error) throw error;
      setSmsBackfillResult(JSON.stringify(data, null, 2));
    } catch (e: any) {
      setSmsBackfillResult(`Error: ${e?.message || String(e)}`);
    } finally {
      setSmsBackfillLoading(false);
    }
  };
  const [errorSearchTerm, setErrorSearchTerm] = useState('');
  const [errorStatusFilter, setErrorStatusFilter] = useState<string>('all');
  const [errorTypeFilter, setErrorTypeFilter] = useState<string>('all');
  const [expandedErrors, setExpandedErrors] = useState<Set<string>>(new Set());
  
  // Delete modal state
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [businessToDelete, setBusinessToDelete] = useState<{ id: string; name: string } | null>(null);
  
  // Reset password state
  const [showResetModal, setShowResetModal] = useState(false);
  const [businessToReset, setBusinessToReset] = useState<{ id: string; name: string; email: string } | null>(null);
  const [resetLoading, setResetLoading] = useState(false);
  const [resetMessage, setResetMessage] = useState<{ type: 'success' | 'error', text: string } | null>(null);
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  
  // Subscription modal state
  const [showSubscriptionModal, setShowSubscriptionModal] = useState(false);
  const [businessForSubscription, setBusinessForSubscription] = useState<{ id: string; name: string } | null>(null);
  const [subscriptionData, setSubscriptionData] = useState<any>(null);
  const [subscriptionLoading, setSubscriptionLoading] = useState(false);
  const [stripeBilling, setStripeBilling] = useState<{
    subscriptions: StripeSubscriptionMirrorRow[];
    invoices: StripeInvoiceMirrorRow[];
  } | null>(null);
  const [stripeBillingSyncing, setStripeBillingSyncing] = useState(false);

  // Stripe invoices tab state
  const [stripeInvoices, setStripeInvoices] = useState<StripeInvoiceMirrorRow[]>([]);
  const [invoicesLoading, setInvoicesLoading] = useState(false);
  const [invoicesSyncing, setInvoicesSyncing] = useState(false);
  const [invoiceSearch, setInvoiceSearch] = useState('');
  const [invoiceStatusFilter, setInvoiceStatusFilter] = useState('all');
  const [invoicesTotal, setInvoicesTotal] = useState(0);
  const [invoicePage, setInvoicePage] = useState(1);
  const [invoiceActionTarget, setInvoiceActionTarget] = useState<StripeInvoiceMirrorRow | null>(null);
  const [invoiceActionLoading, setInvoiceActionLoading] = useState(false);
  const [billingMetrics, setBillingMetrics] = useState<StripeBillingMetrics | null>(null);
  const [billingMetricsLoading, setBillingMetricsLoading] = useState(false);
  const [aiTokenPurchases, setAiTokenPurchases] = useState<AiTokenPurchaseRow[]>([]);
  const [aiTokenPurchasesLoading, setAiTokenPurchasesLoading] = useState(false);
  const [cadRates, setCadRates] = useState<ExchangeRatesToCad>(DEFAULT_CAD_RATES);
  
  // App access grant state
  const [showGrantModal, setShowGrantModal] = useState(false);
  const [appToGrant, setAppToGrant] = useState<{ id: string; name: string } | null>(null);
  const [grantLoading, setGrantLoading] = useState(false);
  const [grantMessage, setGrantMessage] = useState<{ type: 'success' | 'error', text: string } | null>(null);
  
  // App access remove state
  const [showRemoveModal, setShowRemoveModal] = useState(false);
  const [appToRemove, setAppToRemove] = useState<{ id: string; name: string } | null>(null);
  const [removeLoading, setRemoveLoading] = useState(false);
  const [removeMessage, setRemoveMessage] = useState<{ type: 'success' | 'error', text: string } | null>(null);

  // Media (social) connection seats — free trial grants
  const [socialSeats, setSocialSeats] = useState<SocialSeatUsage | null>(null);
  const [socialSeatsLoading, setSocialSeatsLoading] = useState(false);
  const [socialSeatsSaving, setSocialSeatsSaving] = useState(false);
  const [freeSeatsInput, setFreeSeatsInput] = useState(0);
  const [socialSeatsMessage, setSocialSeatsMessage] = useState<{ type: 'success' | 'error', text: string } | null>(null);

  // Requests (super admin) state
  /** Help Centre support requests (support_requests table), enriched with user email + business name. */
  const [requests, setRequests] = useState<any[]>([]);
  const [requestsLoading, setRequestsLoading] = useState(false);

  // Bug reports state
  const [bugReports, setBugReports] = useState<any[]>([]);
  const [bugsLoading, setBugsLoading] = useState(false);
  const [liveBugCount, setLiveBugCount] = useState(0);
  const [liveRequestCount, setLiveRequestCount] = useState(0);
  const { open: openImageViewer } = useImageViewer();

  // Sales dashboard state
  type SalesDateRange = 'this_month' | 'last_month' | 'this_year' | 'last_year';
  const [salesDateRange, setSalesDateRange] = useState<SalesDateRange>('this_month');
  const [salesChartData, setSalesChartData] = useState<any>(null);
  const [salesLoading, setSalesLoading] = useState(false);
  const [salesTotals, setSalesTotals] = useState<{ name: string; total: number }[]>([]);

  // Freeze/unfreeze state
  const [freezingBusinessId, setFreezingBusinessId] = useState<string | null>(null);

  // Expanded business card state
  const [expandedBusinessId, setExpandedBusinessId] = useState<string | null>(null);
  const [expandedDetailsId, setExpandedDetailsId] = useState<string | null>(null);
  const [expandedInvoicesBusinessId, setExpandedInvoicesBusinessId] = useState<string | null>(null);
  const [businessInvoicesById, setBusinessInvoicesById] = useState<Record<string, StripeInvoiceMirrorRow[]>>({});
  const [businessInvoicesLoadingId, setBusinessInvoicesLoadingId] = useState<string | null>(null);

  // Grant complimentary / trial access
  const [showGrantAccessModal, setShowGrantAccessModal] = useState(false);
  const [serviceHoursBusiness, setServiceHoursBusiness] = useState<{ id: string; name: string } | null>(
    null,
  );
  const [businessForGrantAccess, setBusinessForGrantAccess] = useState<{ id: string; name: string } | null>(null);
  const [grantAccessLoading, setGrantAccessLoading] = useState(false);
  const [grantAccessPlans, setGrantAccessPlans] = useState<SubscriptionPlanOption[]>([]);
  const [grantAccessPlansLoading, setGrantAccessPlansLoading] = useState(false);

  // Business search
  const [businessSearch, setBusinessSearch] = useState('');

  // Favorites (persisted in localStorage)
  const [favorites, setFavorites] = useState<Set<string>>(() => {
    try {
      const stored = localStorage.getItem('nexus_admin_favorites');
      return stored ? new Set(JSON.parse(stored)) : new Set();
    } catch { return new Set(); }
  });

  const toggleFavorite = (businessId: string) => {
    setFavorites(prev => {
      const next = new Set(prev);
      if (next.has(businessId)) next.delete(businessId); else next.add(businessId);
      localStorage.setItem('nexus_admin_favorites', JSON.stringify([...next]));
      return next;
    });
  };

  // Portal panel state
  const [portalBusiness, setPortalBusiness] = useState<{ id: string; name: string } | null>(null);

  // Remote access state
  const [showUsersForBusiness, setShowUsersForBusiness] = useState<{ id: string; name: string } | null>(null);
  const [activeRemoteSession, setActiveRemoteSession] = useState<RemoteSession | null>(null);
  const [remoteAccessLoading, setRemoteAccessLoading] = useState(false);

  // Impersonation state
  const [impersonatingBusinessId, setImpersonatingBusinessId] = useState<string | null>(null);
  const [startingSubscriptionBusinessId, setStartingSubscriptionBusinessId] = useState<string | null>(null);
  const [impersonatingUserId, setImpersonatingUserId] = useState<string | null>(null);

  const handleImpersonate = async (business: BusinessSummary) => {
    if (!business.owner_id) {
      alert('Cannot impersonate: no owner found for this business.');
      return;
    }
    setImpersonatingBusinessId(business.id);
    const result = await ImpersonationService.startImpersonation({
      ownerId: business.owner_id,
      businessId: business.id,
      businessName: business.name,
      ownerEmail: business.owner_email,
    });
    if (!result.success) {
      alert(result.message);
      setImpersonatingBusinessId(null);
    }
  };

  /** Direct login as any business user (owner or resource) — same flow as the business Login button. */
  const handleLoginAsUser = async (businessUser: BusinessUser) => {
    if (!showUsersForBusiness || !businessUser.auth_user_id) return;
    setImpersonatingUserId(businessUser.auth_user_id);
    const result = await ImpersonationService.startImpersonation({
      ownerId: businessUser.auth_user_id,
      businessId: showUsersForBusiness.id,
      businessName: showUsersForBusiness.name,
      ownerEmail: businessUser.email,
    });
    if (!result.success) {
      alert(result.message);
      setImpersonatingUserId(null);
    }
  };

  // ── Sales dashboard helpers ──
  const getSalesDateRange = useCallback((range: SalesDateRange) => {
    const now = new Date();
    const startOfMonth = (d: Date) => new Date(d.getFullYear(), d.getMonth(), 1);
    const endOfMonth = (d: Date) => new Date(d.getFullYear(), d.getMonth() + 1, 0);
    switch (range) {
      case 'this_month':
        return { start: startOfMonth(now), end: endOfMonth(now) };
      case 'last_month':
        return { start: startOfMonth(new Date(now.getFullYear(), now.getMonth() - 1)), end: endOfMonth(new Date(now.getFullYear(), now.getMonth() - 1)) };
      case 'this_year':
        return { start: new Date(now.getFullYear(), 0, 1), end: new Date(now.getFullYear(), 11, 31) };
      case 'last_year':
        return { start: new Date(now.getFullYear() - 1, 0, 1), end: new Date(now.getFullYear() - 1, 11, 31) };
    }
  }, []);

  const CHART_COLORS = [
    '#3B82F6', '#EF4444', '#10B981', '#F59E0B', '#8B5CF6',
    '#EC4899', '#06B6D4', '#F97316', '#6366F1', '#14B8A6',
    '#E11D48', '#84CC16', '#0EA5E9', '#D946EF', '#A3E635',
  ];

  const loadSalesDashboard = useCallback(async (range: SalesDateRange) => {
    if (!supabase) return;
    setSalesLoading(true);
    try {
      const { start, end } = getSalesDateRange(range);
      const startStr = start.toISOString().split('T')[0];
      const endStr = end.toISOString().split('T')[0];

      // Fetch all non-deleted, non-cancelled bookings in range across ALL businesses
      const { data: bookings, error } = await supabase
        .from('bookings')
        .select('business_id, subtotal, discount_amount, end_date')
        .eq('is_deleted', false)
        .neq('status', 'cancelled')
        .gte('end_date', startStr)
        .lte('end_date', endStr);

      if (error) throw error;

      // Fetch business names
      const { data: allBusinesses } = await supabase
        .from('businesses')
        .select('id, name');
      const bizMap = new Map((allBusinesses || []).map(b => [b.id, b.name]));

      // Group bookings by business_id
      const byBusiness = new Map<string, { name: string; sales: Map<string, number>; total: number }>();
      for (const b of (bookings || [])) {
        if (!b.end_date || !b.business_id) continue;
        const sale = (b.subtotal || 0) - (b.discount_amount || 0);
        if (!byBusiness.has(b.business_id)) {
          byBusiness.set(b.business_id, { name: bizMap.get(b.business_id) || b.business_id, sales: new Map(), total: 0 });
        }
        const entry = byBusiness.get(b.business_id)!;
        entry.total += sale;

        let key: string;
        if (range === 'this_year' || range === 'last_year') {
          const d = new Date(b.end_date + 'T00:00:00Z');
          key = `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, '0')}`;
        } else {
          key = b.end_date;
        }
        entry.sales.set(key, (entry.sales.get(key) || 0) + sale);
      }

      // Only include businesses that have sales
      const activeBiz = [...byBusiness.entries()]
        .filter(([, v]) => v.total > 0)
        .sort((a, b) => b[1].total - a[1].total);

      // Build labels (all unique date/month keys sorted)
      const allKeys = new Set<string>();
      activeBiz.forEach(([, v]) => v.sales.forEach((_, k) => allKeys.add(k)));

      let labels: string[];
      let labelDisplay: string[];
      if (range === 'this_year' || range === 'last_year') {
        const year = range === 'this_year' ? new Date().getFullYear() : new Date().getFullYear() - 1;
        labels = Array.from({ length: 12 }, (_, i) => `${year}-${String(i + 1).padStart(2, '0')}`);
        labelDisplay = labels.map(m => {
          const [y, mn] = m.split('-');
          return new Date(parseInt(y), parseInt(mn) - 1, 1).toLocaleDateString('en-US', { month: 'short' });
        });
      } else {
        labels = [...allKeys].sort();
        labelDisplay = labels.map(d => {
          const dt = new Date(d + 'T00:00:00Z');
          return dt.toLocaleDateString('en-US', { month: 'short', day: 'numeric', timeZone: 'UTC' });
        });
      }

      // Build datasets — cumulative (running total) so the line shows overall growth
      const datasets = activeBiz.map(([, v], i) => {
        let cumulative = 0;
        const cumulativeData = labels.map(k => {
          cumulative += v.sales.get(k) || 0;
          return cumulative;
        });
        return {
        label: v.name,
        data: cumulativeData,
        borderColor: CHART_COLORS[i % CHART_COLORS.length],
        backgroundColor: CHART_COLORS[i % CHART_COLORS.length] + '18',
        tension: 0.4,
        fill: false,
        pointRadius: 3,
        pointHoverRadius: 6,
        pointBackgroundColor: CHART_COLORS[i % CHART_COLORS.length],
        pointBorderColor: 'white',
        pointBorderWidth: 2,
        borderWidth: 2.5,
      };
      });

      setSalesChartData({ labels: labelDisplay, datasets });
      setSalesTotals(activeBiz.map(([, v]) => ({ name: v.name, total: v.total })));
    } catch (err) {
      console.error('Error loading sales dashboard:', err);
      setSalesChartData(null);
      setSalesTotals([]);
    } finally {
      setSalesLoading(false);
    }
  }, [getSalesDateRange]);

  // Delete business handlers
  const handleDeleteBusiness = (businessId: string, businessName: string) => {
    setBusinessToDelete({ id: businessId, name: businessName });
    setShowDeleteModal(true);
  };

  const handleCloseDeleteModal = () => {
    setShowDeleteModal(false);
    setBusinessToDelete(null);
  };

  const handleBusinessDeleted = () => {
    // Refresh the businesses list after deletion
    loadDashboardData();
  };

  // Reset password handlers
  const handleResetPassword = (businessId: string, businessName: string, businessEmail: string) => {
    console.log('🔍 Reset password clicked:', { businessId, businessName, businessEmail });
    setBusinessToReset({ id: businessId, name: businessName, email: businessEmail });
    setShowResetModal(true);
    setResetMessage(null);
  };

  const handleCloseResetModal = () => {
    setShowResetModal(false);
    setBusinessToReset(null);
    setResetMessage(null);
    setNewPassword('');
    setConfirmPassword('');
  };

  // Subscription modal handlers
  const handleViewSubscription = (businessId: string, businessName: string) => {
    setBusinessForSubscription({ id: businessId, name: businessName });
    setShowSubscriptionModal(true);
    loadSubscriptionData(businessId);
    void loadSocialSeats(businessId);
  };

  const handleCloseSubscriptionModal = () => {
    setShowSubscriptionModal(false);
    setBusinessForSubscription(null);
    setSubscriptionData(null);
    setStripeBilling(null);
    setSocialSeats(null);
    setSocialSeatsMessage(null);
    setFreeSeatsInput(0);
  };

  // Media connection seat handlers (free trial grants)
  const loadSocialSeats = async (businessId: string) => {
    setSocialSeatsLoading(true);
    setSocialSeatsMessage(null);
    try {
      const usage = await SuperAdminService.getSocialSeatUsage(businessId);
      setSocialSeats(usage);
      setFreeSeatsInput(usage.grandfathered_quantity);
    } catch (error) {
      console.error('Error loading media connection seats:', error);
      setSocialSeats(null);
    } finally {
      setSocialSeatsLoading(false);
    }
  };

  const handleSaveFreeSeats = async () => {
    if (!businessForSubscription) return;
    setSocialSeatsSaving(true);
    setSocialSeatsMessage(null);
    try {
      const usage = await SuperAdminService.setFreeSocialSeats(
        businessForSubscription.id,
        freeSeatsInput,
      );
      setSocialSeats(usage);
      setFreeSeatsInput(usage.grandfathered_quantity);
      setSocialSeatsMessage({ type: 'success', text: 'Free media connections updated!' });
    } catch (error) {
      console.error('Error saving media connection seats:', error);
      setSocialSeatsMessage({
        type: 'error',
        text: error instanceof Error ? error.message : 'Failed to update free media connections',
      });
    } finally {
      setSocialSeatsSaving(false);
    }
  };

  // App access grant handlers
  const handleGrantAppAccess = (appId: string, appName: string) => {
    setAppToGrant({ id: appId, name: appName });
    setShowGrantModal(true);
    setGrantMessage(null);
  };

  const handleCloseGrantModal = () => {
    setShowGrantModal(false);
    setAppToGrant(null);
    setGrantMessage(null);
  };

  const handleConfirmGrantAccess = async () => {
    if (!appToGrant || !businessForSubscription) return;
    
    setGrantLoading(true);
    setGrantMessage(null);
    
    try {
      await SuperAdminService.grantAppAccess(businessForSubscription.id, appToGrant.id);
      setGrantMessage({ type: 'success', text: `${appToGrant.name} access granted successfully!` });
      
      // Reload subscription data to update the UI
      await loadSubscriptionData(businessForSubscription.id);
      
      // Close modal after a short delay
      setTimeout(() => {
        handleCloseGrantModal();
      }, 1500);
    } catch (error) {
      console.error('Error granting app access:', error);
      setGrantMessage({ type: 'error', text: error instanceof Error ? error.message : 'Failed to grant app access' });
    } finally {
      setGrantLoading(false);
    }
  };

  // App access remove handlers
  const handleRemoveAppAccess = (appId: string, appName: string) => {
    setAppToRemove({ id: appId, name: appName });
    setShowRemoveModal(true);
    setRemoveMessage(null);
  };

  const handleCloseRemoveModal = () => {
    setShowRemoveModal(false);
    setAppToRemove(null);
    setRemoveMessage(null);
  };

  const handleConfirmRemoveAccess = async () => {
    if (!appToRemove || !businessForSubscription) return;
    
    setRemoveLoading(true);
    setRemoveMessage(null);
    
    try {
      await SuperAdminService.removeAppAccess(businessForSubscription.id, appToRemove.id);
      setRemoveMessage({ type: 'success', text: `${appToRemove.name} access removed successfully!` });
      
      // Reload subscription data to update the UI
      await loadSubscriptionData(businessForSubscription.id);
      
      // Close modal after a short delay
      setTimeout(() => {
        handleCloseRemoveModal();
      }, 1500);
    } catch (error) {
      console.error('Error removing app access:', error);
      setRemoveMessage({ type: 'error', text: error instanceof Error ? error.message : 'Failed to remove app access' });
    } finally {
      setRemoveLoading(false);
    }
  };

  // Freeze/unfreeze handlers
  const handleToggleFreeze = async (businessId: string, currentFrozenStatus: boolean) => {
    setFreezingBusinessId(businessId);
    
    try {
      if (currentFrozenStatus) {
        // Unfreeze
        const result = await SuperAdminService.unfreezeBusiness(businessId);
        if (result.success) {
          // Update local state
          setBusinesses(businesses.map(b => 
            b.id === businessId 
              ? { ...b, frozen: false, frozen_at: undefined, frozen_by: undefined, frozen_reason: undefined }
              : b
          ));
        } else {
          alert(result.message);
        }
      } else {
        // Freeze
        const result = await SuperAdminService.freezeBusiness(businessId);
        if (result.success) {
          // Update local state
          setBusinesses(businesses.map(b => 
            b.id === businessId 
              ? { ...b, frozen: true, frozen_at: new Date().toISOString() }
              : b
          ));
        } else {
          alert(result.message);
        }
      }
    } catch (error) {
      console.error('Error toggling freeze status:', error);
      alert('Failed to update freeze status');
    } finally {
      setFreezingBusinessId(null);
    }
  };

  // Remote access handlers
  const handleRequestRemoteAccess = async (businessUser: BusinessUser) => {
    if (!user || !showUsersForBusiness || !businessUser.auth_user_id) return;

    setRemoteAccessLoading(true);
    try {
      const result = await SuperAdminService.createRemoteSession(
        user.id,
        businessUser.auth_user_id,
        showUsersForBusiness.id
      );

      if (result.success && result.session) {
        setActiveRemoteSession(result.session);
        setShowUsersForBusiness(null);

        // Send push notification to the target user
        await SuperAdminService.sendRemoteAccessNotification(
          businessUser.auth_user_id,
          showUsersForBusiness.id,
          result.session.session_code
        );
      } else {
        alert(result.message);
      }
    } catch (error) {
      console.error('Error requesting remote access:', error);
      alert('Failed to request remote access');
    } finally {
      setRemoteAccessLoading(false);
    }
  };

  const loadSubscriptionData = async (businessId: string) => {
    setSubscriptionLoading(true);
    try {
      const [data, billing] = await Promise.all([
        SuperAdminService.getBusinessSubscriptionData(businessId),
        SuperAdminService.getBusinessStripeBilling(businessId),
      ]);
      setSubscriptionData(data);
      setStripeBilling(billing);
    } catch (error) {
      console.error('Error loading subscription data:', error);
    } finally {
      setSubscriptionLoading(false);
    }
  };

  const handleSyncBusinessStripeBilling = async () => {
    if (!businessForSubscription) return;
    setStripeBillingSyncing(true);
    try {
      await SuperAdminService.syncStripeBillingMirror({ businessId: businessForSubscription.id });
      const billing = await SuperAdminService.getBusinessStripeBilling(businessForSubscription.id);
      setStripeBilling(billing);
      setBusinessInvoicesById((prev) => ({
        ...prev,
        [businessForSubscription.id]: billing.invoices,
      }));
    } catch (error) {
      console.error('Error syncing business Stripe billing:', error);
      alert(error instanceof Error ? error.message : 'Failed to sync Stripe billing');
    } finally {
      setStripeBillingSyncing(false);
    }
  };

  const loadBillingMetrics = useCallback(async () => {
    setBillingMetricsLoading(true);
    try {
      const rates = await getExchangeRatesToCad();
      setCadRates(rates);
      const metrics = await SuperAdminService.getStripeBillingMetrics(rates);
      setBillingMetrics(metrics);
    } catch (error) {
      console.error('Error loading billing metrics:', error);
    } finally {
      setBillingMetricsLoading(false);
    }
  }, []);

  const loadAiTokenPurchases = useCallback(async () => {
    setAiTokenPurchasesLoading(true);
    try {
      const result = await SuperAdminService.listAiTokenPurchases({ page: 1, pageSize: 40 });
      setAiTokenPurchases(result.purchases);
    } catch (error) {
      console.error('Error loading AI token purchases:', error);
    } finally {
      setAiTokenPurchasesLoading(false);
    }
  }, []);

  const loadStripeInvoices = useCallback(async () => {
    setInvoicesLoading(true);
    try {
      const result = await SuperAdminService.listStripeInvoices({
        status: invoiceStatusFilter,
        search: invoiceSearch,
        page: invoicePage,
        pageSize: 50,
      });
      setStripeInvoices(result.invoices);
      setInvoicesTotal(result.total);
    } catch (error) {
      console.error('Error loading Stripe invoices:', error);
    } finally {
      setInvoicesLoading(false);
    }
  }, [invoiceStatusFilter, invoiceSearch, invoicePage]);

  const handleToggleBusinessInvoices = async (businessId: string) => {
    if (expandedInvoicesBusinessId === businessId) {
      setExpandedInvoicesBusinessId(null);
      return;
    }

    setExpandedInvoicesBusinessId(businessId);

    if (businessInvoicesById[businessId]) return;

    setBusinessInvoicesLoadingId(businessId);
    try {
      const billing = await SuperAdminService.getBusinessStripeBilling(businessId);
      setBusinessInvoicesById((prev) => ({ ...prev, [businessId]: billing.invoices }));
    } catch (error) {
      console.error('Error loading business invoices:', error);
    } finally {
      setBusinessInvoicesLoadingId(null);
    }
  };

  const handleOpenGrantAccess = async (businessId: string, businessName: string) => {
    setBusinessForGrantAccess({ id: businessId, name: businessName });
    setShowGrantAccessModal(true);
    setGrantAccessPlansLoading(true);
    try {
      const plans = await SuperAdminService.listSubscriptionPlansForGrant();
      setGrantAccessPlans(plans);
    } catch (error) {
      console.error('Error loading subscription plans:', error);
      setGrantAccessPlans([]);
    } finally {
      setGrantAccessPlansLoading(false);
    }
  };

  const handleCloseGrantAccessModal = () => {
    if (grantAccessLoading) return;
    setShowGrantAccessModal(false);
    setBusinessForGrantAccess(null);
  };

  const handleConfirmGrantBusinessAccess = async (params: {
    planId: string;
    accessMode: GrantAccessMode;
    trialEndDate?: string;
    installAllApps: boolean;
    notes?: string;
  }) => {
    if (!businessForGrantAccess) return;

    setGrantAccessLoading(true);
    try {
      const result = await SuperAdminService.grantBusinessAccess({
        businessId: businessForGrantAccess.id,
        planId: params.planId,
        accessMode: params.accessMode,
        trialEndDate: params.trialEndDate,
        installAllApps: params.installAllApps,
        notes: params.notes,
      });

      if (!result.success) {
        throw new Error(result.message);
      }

      await loadDashboardData();

      if (showSubscriptionModal && businessForSubscription?.id === businessForGrantAccess.id) {
        await loadSubscriptionData(businessForGrantAccess.id);
      }

      alert(
        `${result.message}${result.appsInstalled != null ? ` (${result.appsInstalled} apps installed)` : ''}`,
      );
      handleCloseGrantAccessModal();
    } catch (error) {
      console.error('Error granting business access:', error);
      throw error;
    } finally {
      setGrantAccessLoading(false);
    }
  };

  /** Business is on a Stripe checkout trial (card-based) — eligible for "start subscription now". */
  const hasStripeTrial = (business: BusinessSummary): boolean =>
    (business.subscription_status_raw || '').toLowerCase() === 'trialing' ||
    !!business.stripe_subscriptions?.some((sub) => sub.status === 'trialing' && sub.kind === 'platform');

  const handleStartSubscriptionNow = async (business: BusinessSummary) => {
    const confirmed = window.confirm(
      `Start ${business.name}'s subscription now?\n\nThis ends their trial immediately and charges the card on file for the first invoice today. This cannot be undone.`,
    );
    if (!confirmed) return;

    setStartingSubscriptionBusinessId(business.id);
    try {
      const result = await SuperAdminService.startTrialSubscriptionNow(business.id);
      if (!result.success) {
        alert(`Could not start subscription: ${result.message}`);
        return;
      }
      alert(result.message);
      await loadDashboardData();
    } catch (error) {
      console.error('Error starting subscription:', error);
      alert(`Could not start subscription: ${error instanceof Error ? error.message : 'Unknown error'}`);
    } finally {
      setStartingSubscriptionBusinessId(null);
    }
  };

  const handleSyncAllStripeBilling = async () => {
    setInvoicesSyncing(true);
    try {
      const result = await SuperAdminService.syncStripeBillingMirror({ syncAll: true });
      await Promise.all([loadStripeInvoices(), loadBillingMetrics()]);
      if (activeTab === 'businesses') {
        await loadDashboardData();
      }
      alert(`Synced ${result.invoicesSynced} invoices and ${result.subscriptionsSynced} subscriptions from Stripe.`);
    } catch (error) {
      console.error('Error syncing Stripe billing:', error);
      alert(error instanceof Error ? error.message : 'Failed to sync from Stripe');
    } finally {
      setInvoicesSyncing(false);
    }
  };

  const handleStripeInvoiceAction = async (action: StripeInvoiceAdminAction) => {
    if (!invoiceActionTarget) return;
    setInvoiceActionLoading(true);
    try {
      const result = await SuperAdminService.updateStripeInvoiceStatus(
        invoiceActionTarget.stripe_invoice_id,
        action,
      );
      setInvoiceActionTarget(null);
      await Promise.all([loadStripeInvoices(), loadBillingMetrics()]);
      if (result.deleted) {
        alert('Draft invoice deleted in Stripe.');
      }
    } catch (error) {
      console.error('Error updating Stripe invoice:', error);
      throw error;
    } finally {
      setInvoiceActionLoading(false);
    }
  };

  const formatStripeSubscriptionAmount = (sub: { amount_monthly: number | null; interval: string | null; currency: string }) => {
    if (sub.amount_monthly == null) return null;
    const interval = sub.interval || 'month';
    const cadMonthly = convertDollarsToCad(sub.amount_monthly, sub.currency, cadRates);
    return `${formatCad(cadMonthly)}/${interval}`;
  };

  const handleConfirmResetPassword = async () => {
    if (!businessToReset) return;
    
    console.log('🔍 Confirm reset password:', { 
      businessToReset, 
      newPassword: newPassword ? '[REDACTED]' : 'undefined',
      confirmPassword: confirmPassword ? '[REDACTED]' : 'undefined'
    });
    
    // Validate passwords
    if (!newPassword || newPassword.length < 6) {
      setResetMessage({
        type: 'error',
        text: 'Password must be at least 6 characters long.'
      });
      return;
    }
    
    if (newPassword !== confirmPassword) {
      setResetMessage({
        type: 'error',
        text: 'Passwords do not match.'
      });
      return;
    }
    
    setResetLoading(true);
    setResetMessage(null);
    
    try {
      await SuperAdminService.setPasswordForBusiness(businessToReset.email, newPassword);
      setResetMessage({
        type: 'success',
        text: `Password has been successfully updated for ${businessToReset.name}. They can now log in with the new password.`
      });
      
      // Close modal after 3 seconds
      setTimeout(() => {
        handleCloseResetModal();
      }, 3000);
    } catch (error) {
      console.error('Error setting password:', error);
      setResetMessage({
        type: 'error',
        text: 'Failed to update password. Please try again.'
      });
    } finally {
      setResetLoading(false);
    }
  };


  // Trialling = Stripe `trialing` subscriptions + admin-granted trials.
  const matchesTrialingBusinessTab = (b: BusinessSummary) => !!b.is_trialing;

  const matchesActiveBusinessTab = (b: BusinessSummary) =>
    (b.status === 'active' || b.status === 'past_due') && !matchesTrialingBusinessTab(b);

  const matchesSuspendedBusinessTab = (b: BusinessSummary) =>
    b.status === 'canceled' || b.status === 'inactive' || b.status === 'suspended';

  // Filter businesses based on status and search
  const filteredBusinesses = businesses.filter(business => {
    if (statusFilter === 'active' && !matchesActiveBusinessTab(business)) return false;
    if (statusFilter === 'trialing' && !matchesTrialingBusinessTab(business)) return false;
    if (statusFilter === 'suspended' && !matchesSuspendedBusinessTab(business)) return false;
    if (businessSearch) {
      const q = businessSearch.toLowerCase();
      return business.name.toLowerCase().includes(q) ||
        business.owner_email.toLowerCase().includes(q) ||
        business.owner_name.toLowerCase().includes(q);
    }
    return true;
  });

  const favoriteBusinesses = filteredBusinesses.filter(b => favorites.has(b.id));
  const otherBusinesses = filteredBusinesses.filter(b => !favorites.has(b.id));

  /** Per-filter business counts shown on the status pills. */
  const businessStatusCounts = {
    active: businesses.filter(matchesActiveBusinessTab).length,
    trialing: businesses.filter(matchesTrialingBusinessTab).length,
    suspended: businesses.filter(matchesSuspendedBusinessTab).length,
    all: businesses.length,
  } as const;

  const renderBusinessRow = (business: BusinessSummary) => {
    const isExpanded = expandedBusinessId === business.id;
    const isFav = favorites.has(business.id);
    const showInvoices = expandedInvoicesBusinessId === business.id;
    const businessInvoices = businessInvoicesById[business.id];
    const loadingInvoices = businessInvoicesLoadingId === business.id;
    return (
      <div key={business.id} className="bg-white rounded-[14px] border border-gray-200/60 shadow-[0_1px_3px_rgba(0,0,0,0.04)] overflow-hidden">
        <button
          onClick={() => {
            if (isExpanded) {
              setExpandedBusinessId(null);
              if (expandedInvoicesBusinessId === business.id) {
                setExpandedInvoicesBusinessId(null);
              }
            } else {
              setExpandedBusinessId(business.id);
            }
          }}
          className="w-full text-left p-4 active:bg-gray-50/50 transition-colors touch-manipulation"
        >
          <div className="flex items-start gap-3">
            {business.logo_url ? (
              <img
                src={business.logo_url}
                alt={business.name}
                className="h-10 w-10 rounded-[10px] object-cover flex-shrink-0 bg-gray-100"
              />
            ) : (
              <div className="h-10 w-10 rounded-[10px] flex items-center justify-center flex-shrink-0 bg-gradient-to-br from-purple-500 to-purple-600">
                <span className="text-sm font-bold text-white">{business.name?.charAt(0)?.toUpperCase() || 'B'}</span>
              </div>
            )}
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2">
                <span className="text-sm font-semibold text-gray-900 truncate">{business.name}</span>
                {business.frozen && <Snowflake className="h-3 w-3 text-blue-500 flex-shrink-0" />}
              </div>
              <div className="text-xs text-gray-500 truncate mt-0.5">{business.owner_email || 'No owner'}</div>
            </div>
            <div className="flex flex-col items-end gap-1.5 flex-shrink-0">
              <span
                className={`inline-flex items-center px-2 py-0.5 text-[10px] font-bold rounded-full uppercase tracking-wide ${getStatusColor(business.status, business.admin_access)}`}
              >
                {superAdminBusinessBadgeLabel(business.status, business.admin_access)}
              </span>
              <button
                onClick={(e) => { e.stopPropagation(); toggleFavorite(business.id); }}
                className="p-1 rounded-full active:scale-90 transition-transform touch-manipulation"
                aria-label={isFav ? 'Remove from favorites' : 'Add to favorites'}
              >
                <Star className={`h-4 w-4 transition-colors ${isFav ? 'text-amber-400 fill-amber-400' : 'text-gray-300'}`} />
              </button>
            </div>
          </div>

          <div className="flex items-start gap-2 mt-3">
            <div className="flex-1 min-w-0">
              {(business.admin_access || (business.stripe_subscriptions && business.stripe_subscriptions.length > 0)) ? (
                <div className="flex flex-col gap-1.5">
                  {business.admin_access && business.plan_name !== 'No Plan' && (
                    <div
                      className="flex items-center justify-between gap-2 rounded-[10px] px-2.5 py-2 border border-emerald-200/80"
                      style={{ background: 'rgba(16,185,129,0.08)' }}
                    >
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-1.5">
                          <Gift className="h-3 w-3 text-emerald-600 flex-shrink-0" />
                          <p className="text-[12px] font-semibold text-gray-900 truncate">
                            {business.plan_name}
                          </p>
                        </div>
                        <div className="flex items-center gap-1.5 mt-0.5 flex-wrap pl-[18px]">
                          <span className="inline-flex px-1.5 py-0.5 text-[9px] font-bold rounded-full uppercase tracking-wide bg-emerald-100 text-emerald-700">
                            {business.admin_access.kind === 'trial' ? 'Trial' : 'Granted'}
                          </span>
                          {adminAccessTimingLine(business.admin_access) && (
                            <span className="text-[10px] text-emerald-700/80">
                              {adminAccessTimingLine(business.admin_access)}
                            </span>
                          )}
                        </div>
                      </div>
                      {formatPlatformPlanPrice(business.platform_plan_price) && (
                        <span className="text-[12px] font-semibold text-gray-900 tabular-nums flex-shrink-0">
                          {formatPlatformPlanPrice(business.platform_plan_price)}
                        </span>
                      )}
                    </div>
                  )}
                  {business.stripe_subscriptions?.map((sub) => {
                      const amountLabel = formatStripeSubscriptionAmount(sub);
                      const timingLines = stripeSubTimingLines(sub);
                      return (
                        <div
                          key={sub.stripe_subscription_id}
                          className="flex items-center justify-between gap-2 rounded-[10px] px-2.5 py-2"
                          style={{ background: 'rgba(118,118,128,0.08)' }}
                        >
                          <div className="min-w-0 flex-1">
                            <p className="text-[12px] font-semibold text-gray-900 truncate">
                              {sub.plan_label || stripeSubscriptionKindLabel(sub.kind)}
                            </p>
                            <div className="flex items-center gap-1.5 mt-0.5 flex-wrap">
                              <span className={`inline-flex px-1.5 py-0.5 text-[9px] font-bold rounded-full uppercase tracking-wide ${stripeSubscriptionStatusClass(sub.status)}`}>
                                {sub.status}
                              </span>
                              {timingLines.map((line) => (
                                <span key={line} className="text-[10px] text-gray-400">
                                  {line}
                                </span>
                              ))}
                            </div>
                          </div>
                        {amountLabel && (
                          <span className="text-[12px] font-semibold text-gray-900 tabular-nums flex-shrink-0">
                            {amountLabel}
                          </span>
                        )}
                      </div>
                    );
                  })}
                </div>
              ) : (
                <p className="text-[12px] text-gray-400 px-0.5">No active platform access</p>
              )}
            </div>
            <ChevronRight
              className={`h-4 w-4 text-gray-300 flex-shrink-0 mt-1 transition-transform duration-200 ${isExpanded ? 'rotate-90' : ''}`}
            />
          </div>
        </button>

        {isExpanded && (
          <div className="px-4 pb-4 border-t border-gray-100">
            {/* Quick action icon buttons */}
            <div className="flex items-center justify-center gap-3 mt-3 mb-3">
              <button
                onClick={() => handleImpersonate(business)}
                disabled={impersonatingBusinessId === business.id}
                className="flex flex-col items-center gap-1 touch-manipulation disabled:opacity-50"
                style={{ WebkitTapHighlightColor: 'transparent' }}
              >
                <div className={`h-11 w-11 rounded-full flex items-center justify-center active:scale-90 transition-transform ${
                  impersonatingBusinessId === business.id ? 'bg-green-100' : 'bg-green-50'
                }`}>
                  {impersonatingBusinessId === business.id ? (
                    <div className="animate-spin rounded-full h-5 w-5 border-2 border-green-600 border-t-transparent" />
                  ) : (
                    <LogIn className="h-5 w-5 text-green-600" />
                  )}
                </div>
                <span className="text-[11px] font-medium text-gray-500">Login</span>
              </button>
              <button
                onClick={() => setShowUsersForBusiness({ id: business.id, name: business.name })}
                className="flex flex-col items-center gap-1 touch-manipulation"
                style={{ WebkitTapHighlightColor: 'transparent' }}
              >
                <div className="h-11 w-11 rounded-full bg-indigo-50 flex items-center justify-center active:scale-90 transition-transform">
                  <Monitor className="h-5 w-5 text-indigo-600" />
                </div>
                <span className="text-[11px] font-medium text-gray-500">Remote</span>
              </button>
              <button
                onClick={() => handleResetPassword(business.id, business.name, business.owner_email)}
                className="flex flex-col items-center gap-1 touch-manipulation"
                style={{ WebkitTapHighlightColor: 'transparent' }}
              >
                <div className="h-11 w-11 rounded-full bg-blue-50 flex items-center justify-center active:scale-90 transition-transform">
                  <Key className="h-5 w-5 text-blue-600" />
                </div>
                <span className="text-[11px] font-medium text-gray-500">Password</span>
              </button>
              <button
                onClick={() => handleToggleFreeze(business.id, business.frozen)}
                disabled={freezingBusinessId === business.id}
                className="flex flex-col items-center gap-1 touch-manipulation disabled:opacity-50"
                style={{ WebkitTapHighlightColor: 'transparent' }}
              >
                <div className={`h-11 w-11 rounded-full flex items-center justify-center active:scale-90 transition-transform ${
                  business.frozen ? 'bg-blue-50' : 'bg-orange-50'
                }`}>
                  {freezingBusinessId === business.id ? (
                    <div className="animate-spin rounded-full h-5 w-5 border-2 border-orange-600 border-t-transparent" />
                  ) : business.frozen ? (
                    <Unlock className="h-5 w-5 text-blue-600" />
                  ) : (
                    <Snowflake className="h-5 w-5 text-orange-600" />
                  )}
                </div>
                <span className="text-[11px] font-medium text-gray-500">{business.frozen ? 'Unfreeze' : 'Freeze'}</span>
              </button>
              <button
                onClick={() => setPortalBusiness({ id: business.id, name: business.name })}
                className="flex flex-col items-center gap-1 touch-manipulation"
                style={{ WebkitTapHighlightColor: 'transparent' }}
              >
                <div className="h-11 w-11 rounded-full bg-purple-50 flex items-center justify-center active:scale-90 transition-transform">
                  <Megaphone className="h-5 w-5 text-purple-600" />
                </div>
                <span className="text-[11px] font-medium text-gray-500">Portal</span>
              </button>
              <button
                onClick={() => handleToggleBusinessInvoices(business.id)}
                className="flex flex-col items-center gap-1 touch-manipulation"
                style={{ WebkitTapHighlightColor: 'transparent' }}
              >
                <div className={`h-11 w-11 rounded-full flex items-center justify-center active:scale-90 transition-transform ${
                  showInvoices ? 'bg-teal-100' : 'bg-teal-50'
                }`}>
                  {loadingInvoices ? (
                    <div className="animate-spin rounded-full h-5 w-5 border-2 border-teal-600 border-t-transparent" />
                  ) : (
                    <Receipt className={`h-5 w-5 ${showInvoices ? 'text-teal-700' : 'text-teal-600'}`} />
                  )}
                </div>
                <span className={`text-[11px] font-medium ${showInvoices ? 'text-teal-700' : 'text-gray-500'}`}>Invoices</span>
              </button>
              <button
                onClick={() => setServiceHoursBusiness({ id: business.id, name: business.name })}
                className="flex flex-col items-center gap-1 touch-manipulation"
                style={{ WebkitTapHighlightColor: 'transparent' }}
              >
                <div className="h-11 w-11 rounded-full bg-blue-50 flex items-center justify-center active:scale-90 transition-transform">
                  <Clock className="h-5 w-5 text-blue-600" />
                </div>
                <span className="text-[11px] font-medium text-gray-500">Hours</span>
              </button>
            </div>

            {showInvoices && (
              <div className="rounded-[10px] overflow-hidden mb-3" style={{ background: 'rgba(118,118,128,0.08)' }}>
                {loadingInvoices && !businessInvoices ? (
                  <div className="flex items-center justify-center py-6">
                    <Loader2 className="h-5 w-5 animate-spin text-teal-600" />
                  </div>
                ) : businessInvoices && businessInvoices.length > 0 ? (
                  businessInvoices.map((inv, index) => (
                    <div key={inv.id} className={`px-3 py-2.5 ${index > 0 ? 'border-t border-gray-200/40' : ''}`}>
                      <div className="flex items-center justify-between gap-2">
                        <div className="min-w-0">
                          <p className="text-[14px] font-medium text-gray-900 truncate">
                            {inv.number ? `#${inv.number}` : inv.stripe_invoice_id.slice(0, 16)}
                          </p>
                          <p className="text-[12px] text-gray-500">
                            {new Date(inv.invoice_created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                          </p>
                        </div>
                        <div className="text-right flex-shrink-0">
                          <p className="text-[14px] font-semibold text-gray-900">
                            {formatMirrorInvoiceAmountCad(inv.amount_paid || inv.amount_due, inv.currency, cadRates)}
                          </p>
                          <div className="flex items-center justify-end gap-2 mt-0.5">
                            <span className={`inline-flex px-1.5 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wide ${stripeInvoiceStatusClass(inv.status)}`}>
                              {inv.status}
                            </span>
                            {(inv.hosted_invoice_url || inv.invoice_pdf) && (
                              <a
                                href={inv.hosted_invoice_url || inv.invoice_pdf || '#'}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-[11px] font-semibold text-purple-600"
                              >
                                PDF
                              </a>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  ))
                ) : (
                  <p className="text-[13px] text-gray-400 px-3 py-4 text-center">No Stripe invoices synced yet.</p>
                )}
              </div>
            )}

            {/* List actions: View Subscription, Grant Access & Delete */}
            <div className="rounded-[10px] overflow-hidden mb-3" style={{ background: 'rgba(118,118,128,0.08)' }}>
              <button onClick={() => handleViewSubscription(business.id, business.name)} className="w-full flex items-center gap-3 px-3 py-2.5 active:bg-black/[0.04] touch-manipulation" style={{ WebkitTapHighlightColor: 'transparent' }}>
                <CreditCard className="h-4 w-4 text-purple-600" />
                <span className="text-[15px] font-medium text-gray-900 flex-1 text-left">View Subscription</span>
                <ChevronRight className="h-4 w-4 text-gray-300" />
              </button>
              {hasStripeTrial(business) && (
                <>
                  <div className="border-t border-gray-200/40" />
                  <button
                    onClick={() => handleStartSubscriptionNow(business)}
                    disabled={startingSubscriptionBusinessId === business.id}
                    className="w-full flex items-center gap-3 px-3 py-2.5 active:bg-black/[0.04] touch-manipulation disabled:opacity-50"
                    style={{ WebkitTapHighlightColor: 'transparent' }}
                  >
                    {startingSubscriptionBusinessId === business.id ? (
                      <Loader2 className="h-4 w-4 text-amber-600 animate-spin" />
                    ) : (
                      <Zap className="h-4 w-4 text-amber-600" />
                    )}
                    <span className="text-[15px] font-medium text-gray-900 flex-1 text-left">
                      {startingSubscriptionBusinessId === business.id ? 'Starting Subscription…' : 'Start Subscription Now'}
                    </span>
                    <span className="text-[11px] font-semibold text-amber-600 uppercase tracking-wide">Ends trial</span>
                  </button>
                </>
              )}
              <div className="border-t border-gray-200/40" />
              <button
                onClick={() => handleOpenGrantAccess(business.id, business.name)}
                className="w-full flex items-center gap-3 px-3 py-2.5 active:bg-black/[0.04] touch-manipulation"
                style={{ WebkitTapHighlightColor: 'transparent' }}
              >
                <Gift className="h-4 w-4 text-green-600" />
                <span className="text-[15px] font-medium text-gray-900 flex-1 text-left">Grant Access</span>
                <ChevronRight className="h-4 w-4 text-gray-300" />
              </button>
              <div className="border-t border-gray-200/40" />
              <button
                onClick={() => setServiceHoursBusiness({ id: business.id, name: business.name })}
                className="w-full flex items-center gap-3 px-3 py-2.5 active:bg-black/[0.04] touch-manipulation"
                style={{ WebkitTapHighlightColor: 'transparent' }}
              >
                <Clock className="h-4 w-4 text-blue-600" />
                <span className="text-[15px] font-medium text-gray-900 flex-1 text-left">Service hours</span>
                <ChevronRight className="h-4 w-4 text-gray-300" />
              </button>
              <div className="border-t border-gray-200/40" />
              <button onClick={() => handleDeleteBusiness(business.id, business.name)} className="w-full flex items-center gap-3 px-3 py-2.5 active:bg-black/[0.04] touch-manipulation" style={{ WebkitTapHighlightColor: 'transparent' }}>
                <Trash2 className="h-4 w-4 text-red-600" />
                <span className="text-[15px] font-medium text-red-600 flex-1 text-left">Delete Business</span>
              </button>
            </div>

            {/* Collapsible business details */}
            <button
              onClick={() => setExpandedDetailsId(expandedDetailsId === business.id ? null : business.id)}
              className="w-full flex items-center gap-2 px-1 py-2 touch-manipulation"
              style={{ WebkitTapHighlightColor: 'transparent' }}
            >
              <ChevronDown className={`h-4 w-4 text-gray-400 transition-transform duration-200 ${expandedDetailsId === business.id ? 'rotate-180' : ''}`} />
              <span className="text-[13px] font-semibold text-gray-400 uppercase tracking-wide">Details</span>
            </button>
            {expandedDetailsId === business.id && (
              <div className="rounded-[10px] overflow-hidden" style={{ background: 'rgba(118,118,128,0.08)' }}>
                <div className="flex items-center justify-between px-3 py-2.5">
                  <span className="text-[13px] text-gray-500">Owner</span>
                  <span className="text-[13px] font-medium text-gray-900 truncate ml-4">{business.owner_name || business.owner_email || '—'}</span>
                </div>
                <div className="border-t border-gray-200/40" />
                <div className="flex items-center justify-between px-3 py-2.5">
                  <span className="text-[13px] text-gray-500">Email</span>
                  <span className="text-[13px] font-medium text-gray-900 truncate ml-4">{business.owner_email || '—'}</span>
                </div>
                {business.phone && (
                  <>
                    <div className="border-t border-gray-200/40" />
                    <div className="flex items-center justify-between px-3 py-2.5">
                      <span className="text-[13px] text-gray-500">Phone</span>
                      <span className="text-[13px] font-medium text-gray-900">{business.phone}</span>
                    </div>
                  </>
                )}
                <div className="border-t border-gray-200/40" />
                <div className="flex items-center justify-between px-3 py-2.5">
                  <span className="text-[13px] text-gray-500">Joined</span>
                  <span className="text-[13px] font-medium text-gray-900">{formatJoinedDate(business.created_at)}</span>
                </div>
                {business.addon_apps && business.addon_apps.length > 0 && (
                  <>
                    <div className="border-t border-gray-200/40" />
                    <div className="flex items-center justify-between px-3 py-2.5">
                      <span className="text-[13px] text-gray-500">Add-ons</span>
                      <span className="text-[13px] font-medium text-gray-900 truncate ml-4">{business.addon_apps.join(', ')}</span>
                    </div>
                  </>
                )}
                <div className="border-t border-gray-200/40" />
                <div className="flex items-center justify-between px-3 py-2.5">
                  <span className="text-[13px] text-gray-500">Integrations</span>
                  <div className="flex items-center gap-1.5">
                    {business.integrations.gmail && <span className="flex items-center gap-0.5 text-[10px] font-semibold text-green-700 bg-green-100 px-1.5 py-0.5 rounded-full"><Mail className="h-2.5 w-2.5" />Gmail</span>}
                    {business.integrations.twilio && <span className="flex items-center gap-0.5 text-[10px] font-semibold text-blue-700 bg-blue-100 px-1.5 py-0.5 rounded-full"><PhoneIcon className="h-2.5 w-2.5" />Twilio</span>}
                    {business.integrations.stripe && <span className="flex items-center gap-0.5 text-[10px] font-semibold text-purple-700 bg-purple-100 px-1.5 py-0.5 rounded-full"><Zap className="h-2.5 w-2.5" />Stripe</span>}
                    {!business.integrations.gmail && !business.integrations.twilio && !business.integrations.stripe && <span className="text-[13px] text-gray-400">None</span>}
                  </div>
                </div>
                <SuperAdminNexusWebsiteRow
                  business={business}
                  onUpdated={(id, patch) =>
                    setBusinesses((prev) =>
                      prev.map((b) => (b.id === id ? { ...b, ...patch } : b))
                    )
                  }
                />
              </div>
            )}
          </div>
        )}
      </div>
    );
  };

  // Load activity logs
  const loadActivityLogs = async () => {
    setActivityLoading(true);
    try {
      // First, fetch activity logs with business join (businesses is in public schema)
      // Note: We can't join auth.users directly, so we'll fetch user emails separately if needed
      let query = supabase
        .from('activity_logs')
        .select(`
          *,
          business:businesses!activity_logs_business_id_fkey(name)
        `)
        .order('created_at', { ascending: false })
        .limit(1000);

      // Apply filters
      if (activityFilter.activityType) {
        query = query.eq('activity_type', activityFilter.activityType);
      }
      if (activityFilter.activityCategory) {
        query = query.eq('activity_category', activityFilter.activityCategory);
      }
      if (activityFilter.userId) {
        query = query.eq('user_id', activityFilter.userId);
      }
      if (activityFilter.businessId) {
        query = query.eq('business_id', activityFilter.businessId);
      }
      if (activityFilter.dateFrom) {
        query = query.gte('created_at', activityFilter.dateFrom);
      }
      if (activityFilter.dateTo) {
        query = query.lte('created_at', activityFilter.dateTo);
      }

      const { data: logs, error } = await query;

      if (error) throw error;

      // Enrich with user emails from public.users table
      const userIds = [...new Set((logs || []).map(log => log.user_id).filter(Boolean))];
      const userEmailMap = new Map<string, string>();
      
      // Fetch user emails from public.users table
      if (userIds.length > 0) {
        try {
          const { data: users, error: usersError } = await supabase
            .from('users')
            .select('id, email')
            .in('id', userIds);
          
          if (!usersError && users) {
            users.forEach(user => {
              if (user.email) {
                userEmailMap.set(user.id, user.email);
              }
            });
          }
        } catch (err) {
          console.warn('Failed to fetch user emails:', err);
          // Continue without user emails - we'll show user_id as fallback
        }
      }
      
      // Enrich logs with user emails
      const enrichedLogs = (logs || []).map((log) => {
        const userEmail = log.user_id ? userEmailMap.get(log.user_id) : null;
        return {
          ...log,
          user: userEmail ? { email: userEmail } : null,
        };
      });

      // Apply search filter
      let filteredData = enrichedLogs;
      if (activitySearch) {
        const searchLower = activitySearch.toLowerCase();
        filteredData = filteredData.filter(log => 
          log.page_name?.toLowerCase().includes(searchLower) ||
          log.page_path?.toLowerCase().includes(searchLower) ||
          log.action?.toLowerCase().includes(searchLower) ||
          log.user?.email?.toLowerCase().includes(searchLower) ||
          log.business?.name?.toLowerCase().includes(searchLower)
        );
      }

      setActivityLogs(filteredData);
    } catch (error) {
      console.error('Error loading activity logs:', error);
      alert('Failed to load activity logs');
    } finally {
      setActivityLoading(false);
    }
  };

  // Load sales dashboard when tab is active or date range changes
  useEffect(() => {
    if (activeTab === 'dashboard') {
      loadSalesDashboard(salesDateRange);
    }
  }, [activeTab, salesDateRange, loadSalesDashboard]);

  // Load activity logs when tab is active
  useEffect(() => {
    if (activeTab === 'activity') {
      loadActivityLogs();
    }
  }, [activeTab, activityFilter, activitySearch]);

  // Load support requests when tab is active (Help Centre → Support Request).
  const loadRequests = async () => {
    if (!supabase) return;
    setRequestsLoading(true);
    try {
      const { data, error } = await supabase
        .from('support_requests')
        .select('*')
        .neq('status', 'closed')
        .order('created_at', { ascending: false });
      if (error) throw error;

      const userIds = [...new Set((data || []).map((r: any) => r.user_id))];
      const businessIds = [...new Set((data || []).filter((r: any) => r.business_id).map((r: any) => r.business_id))];

      let emailMap: Record<string, string> = {};
      if (userIds.length > 0) {
        const { data: users } = await supabase.from('users').select('id, email').in('id', userIds);
        if (users) emailMap = Object.fromEntries(users.map((u: any) => [u.id, u.email]));
      }
      let businessMap: Record<string, string> = {};
      if (businessIds.length > 0) {
        const { data: businesses } = await supabase.from('businesses').select('id, name').in('id', businessIds);
        if (businesses) businessMap = Object.fromEntries(businesses.map((b: any) => [b.id, b.name]));
      }

      // Conversation threads for all loaded tickets in one query.
      const requestIds = (data || []).map((r: any) => r.id);
      let messagesByRequest: Record<string, any[]> = {};
      if (requestIds.length > 0) {
        const { data: msgs } = await supabase
          .from('support_request_messages')
          .select('id, request_id, sender, body, created_at')
          .in('request_id', requestIds)
          .order('created_at', { ascending: true });
        for (const m of msgs || []) {
          (messagesByRequest[m.request_id] ||= []).push(m);
        }
      }

      setRequests(
        (data || []).map((req: any) => ({
          ...req,
          user_email: emailMap[req.user_id] || 'Unknown',
          business_name: req.business_id ? (businessMap[req.business_id] || 'Unknown') : null,
          messages: messagesByRequest[req.id] || [],
        })) as any,
      );
    } catch (error) {
      console.error('Error loading support requests:', error);
    } finally {
      setRequestsLoading(false);
    }
  };

  // Push-notify the submitter that the Nexus team responded to their ticket.
  const notifyTicketUser = async (
    userId: string,
    businessId: string | null,
    title: string,
    body: string,
  ) => {
    if (!supabase) return;
    try {
      await supabase.functions.invoke('send-push-notification', {
        body: {
          notification: { title, body, data: { type: 'support_reply' } },
          user_id: userId,
          ...(businessId ? { business_id: businessId } : {}),
        },
      });
    } catch (e) {
      console.warn('Ticket push notification failed (non-critical):', e);
    }
  };

  // Ticket reply drafts (shared by Requests + Bugs tabs), keyed by row id.
  const [ticketReplyOpenId, setTicketReplyOpenId] = useState<string | null>(null);
  const [ticketReplyDraft, setTicketReplyDraft] = useState('');
  const [ticketReplyBusy, setTicketReplyBusy] = useState(false);

  const sendSupportReply = async (req: any) => {
    if (!supabase || !ticketReplyDraft.trim()) return;
    setTicketReplyBusy(true);
    try {
      const { data: authData } = await supabase.auth.getUser();
      // Thread message; a trigger flips the ticket to 'replied' + stamps replied_at.
      const { error } = await supabase.from('support_request_messages').insert({
        request_id: req.id,
        sender: 'admin',
        sender_user_id: authData?.user?.id ?? null,
        body: ticketReplyDraft.trim(),
      });
      if (error) throw error;
      await notifyTicketUser(
        req.user_id,
        req.business_id,
        'Nexus support replied',
        `Your support request${req.ticket_number ? ` ${req.ticket_number}` : ''} has a new reply. Open Help to view it.`,
      );
      setTicketReplyOpenId(null);
      setTicketReplyDraft('');
      loadRequests();
    } catch (e) {
      console.error('Failed to send support reply:', e);
      alert('Failed to send reply. Is the support ticket migration applied?');
    } finally {
      setTicketReplyBusy(false);
    }
  };

  const closeSupportRequest = async (req: any) => {
    if (!supabase) return;
    try {
      const { error } = await supabase
        .from('support_requests')
        .update({ status: 'closed', resolved_at: new Date().toISOString() })
        .eq('id', req.id);
      if (error) throw error;
      loadRequests();
    } catch (e) {
      console.error('Failed to close support request:', e);
    }
  };

  const sendBugReply = async (bug: any) => {
    if (!supabase || !ticketReplyDraft.trim()) return;
    setTicketReplyBusy(true);
    try {
      const { error } = await supabase
        .from('user_bugs')
        .update({
          admin_reply: ticketReplyDraft.trim(),
          replied_at: new Date().toISOString(),
          ...(bug.status === 'new' ? { status: 'investigating' } : {}),
        })
        .eq('id', bug.id);
      if (error) throw error;
      await notifyTicketUser(
        bug.user_id,
        bug.business_id,
        'Nexus team replied to your bug report',
        'Your bug report has a reply. Open Help to view it.',
      );
      setTicketReplyOpenId(null);
      setTicketReplyDraft('');
      loadBugReports();
    } catch (e) {
      console.error('Failed to send bug reply:', e);
      alert('Failed to send reply. Is the support ticket migration applied?');
    } finally {
      setTicketReplyBusy(false);
    }
  };

  useEffect(() => {
    getExchangeRatesToCad().then(setCadRates).catch(() => {});
  }, []);

  useEffect(() => {
    if (activeTab === 'requests') {
      loadRequests();
    }
  }, [activeTab]);

  useEffect(() => {
    if (activeTab === 'invoices') {
      loadStripeInvoices();
      loadBillingMetrics();
      loadAiTokenPurchases();
    }
  }, [activeTab, loadStripeInvoices, loadBillingMetrics, loadAiTokenPurchases]);

  // Load push notification delivery logs when the Push tab is active or its filter changes
  useEffect(() => {
    if (activeTab === 'push') {
      loadPushLogs();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, pushStatusFilter]);

  // Load bug reports when tab is active
  const loadBugReports = async () => {
    if (!supabase) return;
    setBugsLoading(true);
    try {
      const { data, error } = await supabase
        .from('user_bugs')
        .select('*')
        .neq('status', 'fixed')
        .order('created_at', { ascending: false });

      if (error) throw error;

      const userIds = [...new Set((data || []).map((b: any) => b.user_id))];
      const businessIds = [...new Set((data || []).filter((b: any) => b.business_id).map((b: any) => b.business_id))];

      let emailMap: Record<string, string> = {};
      if (userIds.length > 0) {
        const { data: users } = await supabase
          .from('users')
          .select('id, email')
          .in('id', userIds);
        if (users) {
          emailMap = Object.fromEntries(users.map((u: any) => [u.id, u.email]));
        }
      }

      let businessMap: Record<string, string> = {};
      if (businessIds.length > 0) {
        const { data: businesses } = await supabase
          .from('businesses')
          .select('id, name')
          .in('id', businessIds);
        if (businesses) {
          businessMap = Object.fromEntries(businesses.map((b: any) => [b.id, b.name]));
        }
      }

      setBugReports(
        (data || []).map((bug: any) => ({
          ...bug,
          user_email: emailMap[bug.user_id] || 'Unknown',
          business_name: bug.business_id ? (businessMap[bug.business_id] || 'Unknown') : null,
        }))
      );
    } catch (error) {
      console.error('Error loading bug reports:', error);
    } finally {
      setBugsLoading(false);
    }
  };

  const updateBugStatus = async (bugId: string, newStatus: string) => {
    if (!supabase) return;
    try {
      let { error } = await supabase
        .from('user_bugs')
        .update({
          status: newStatus,
          ...(newStatus === 'fixed' ? { resolved_at: new Date().toISOString() } : {}),
        })
        .eq('id', bugId);

      // Pre-migration fallback: resolved_at column may not exist yet.
      if (error && newStatus === 'fixed') {
        ({ error } = await supabase.from('user_bugs').update({ status: newStatus }).eq('id', bugId));
      }
      if (error) throw error;

      // Tell the reporter their bug is fixed (shows in their Help Centre too).
      if (newStatus === 'fixed') {
        const bug = bugReports.find((b: any) => b.id === bugId);
        if (bug) {
          void notifyTicketUser(
            bug.user_id,
            bug.business_id,
            'Your bug report was fixed',
            'A bug you reported has been marked as fixed. Thanks for helping improve Nexus!',
          );
        }
      }
      loadBugReports();
    } catch (error) {
      console.error('Error updating bug status:', error);
    }
  };

  useEffect(() => {
    if (activeTab === 'bugs') {
      loadBugReports();
    }
  }, [activeTab]);

  // Live bug count: fetch initial + subscribe to realtime changes
  const fetchBugCount = async () => {
    if (!supabase) return;
    const { count, error } = await supabase
      .from('user_bugs')
      .select('*', { count: 'exact', head: true })
      .not('status', 'in', '("pending","fixed")');
    if (!error && count !== null) setLiveBugCount(count);
  };

  // Live open support-request count for the Requests tab badge.
  const fetchRequestCount = async () => {
    if (!supabase) return;
    const { count, error } = await supabase
      .from('support_requests')
      .select('*', { count: 'exact', head: true })
      .eq('status', 'open');
    if (!error && count !== null) setLiveRequestCount(count);
  };

  useEffect(() => {
    if (!supabase || !isSuperAdmin) return;

    fetchBugCount();
    fetchRequestCount();

    const channel = supabase
      .channel('user_bugs_badge')
      .on('postgres_changes', { event: '*', schema: 'public', table: 'user_bugs' }, () => {
        fetchBugCount();
        if (activeTabRef.current === 'bugs') loadBugReports();
      })
      .on('postgres_changes', { event: '*', schema: 'public', table: 'support_requests' }, () => {
        fetchRequestCount();
        if (activeTabRef.current === 'requests') loadRequests();
      })
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [isSuperAdmin]);

  // Load dashboard data
  const loadDashboardData = async () => {
    if (typeof window !== 'undefined') {
      const params = new URLSearchParams(window.location.search);
      if (params.get('nexus_hosting') === 'activated') {
        const checkoutSessionId = params.get('session_id');
        params.delete('nexus_hosting');
        params.delete('session_id');
        const qs = params.toString();
        window.history.replaceState(
          {},
          '',
          `${window.location.pathname}${qs ? `?${qs}` : ''}`,
        );
        if (checkoutSessionId) {
          try {
            const r = (await handleStripeCheckoutSuccess(checkoutSessionId)) as { success?: boolean };
            if (!r?.success) {
              console.error(
                'Nexus hosting checkout: server did not confirm session; data may still sync via webhook.',
              );
            }
          } catch (e) {
            console.error('Nexus hosting checkout: session verify failed', e);
          }
        }
      }
    }
    setLoading(true);
    try {
      const businessesData = await SuperAdminService.getAllBusinesses();
      setBusinesses(businessesData);
    } catch (error) {
      console.error('Error loading dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  // Load error logs
  const loadPushLogs = async () => {
    try {
      setPushLogsLoading(true);

      let query = supabase
        .from('push_notification_log')
        .select('*')
        .order('created_at', { ascending: false })
        .limit(500);

      if (pushStatusFilter === 'success') query = query.eq('success', true);
      if (pushStatusFilter === 'failed') query = query.eq('success', false);

      const { data, error } = await query;

      if (error) {
        console.error('Error loading push logs:', error);
        return;
      }

      setPushLogs(data || []);
    } catch (error) {
      console.error('Error loading push logs:', error);
    } finally {
      setPushLogsLoading(false);
    }
  };

  const loadErrorLogs = async () => {
    try {
      setErrorLogsLoading(true);
      
      let query = supabase
        .from('error_logs')
        .select('*')
        .order('created_at', { ascending: false })
        .limit(500);
      
      if (errorStatusFilter !== 'all') {
        query = query.eq('status', errorStatusFilter);
      }
      
      if (errorTypeFilter !== 'all') {
        query = query.eq('error_type', errorTypeFilter);
      }
      
      const { data, error } = await query;
      
      if (error) {
        console.error('Error loading error logs:', error);
        return;
      }
      
      setErrorLogs(data || []);
    } catch (error) {
      console.error('Error loading error logs:', error);
    } finally {
      setErrorLogsLoading(false);
    }
  };

  // Update error status
  const updateErrorStatus = async (errorId: string, status: 'new' | 'investigating' | 'resolved' | 'ignored') => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      
      const updateData: any = {
        status,
        last_seen_at: new Date().toISOString()
      };
      
      if (status === 'resolved') {
        updateData.resolved_at = new Date().toISOString();
        updateData.resolved_by = user?.id;
      }
      
      const { error } = await supabase
        .from('error_logs')
        .update(updateData)
        .eq('id', errorId);
      
      if (error) {
        console.error('Error updating error status:', error);
        return;
      }
      
      loadErrorLogs();
    } catch (error) {
      console.error('Error updating error status:', error);
    }
  };

  const toggleErrorExpand = (errorId: string) => {
    const newExpanded = new Set(expandedErrors);
    if (newExpanded.has(errorId)) {
      newExpanded.delete(errorId);
    } else {
      newExpanded.add(errorId);
    }
    setExpandedErrors(newExpanded);
  };


  useEffect(() => {
    if (!authLoading && isSuperAdmin) {
      loadDashboardData();
    }
  }, [authLoading, isSuperAdmin]);



  // Redirect if not super admin
  if (authLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-[#f2f2f7]" style={{ fontFamily: '-apple-system, BlinkMacSystemFont, "SF Pro Display", "SF Pro Text", system-ui, sans-serif' }}>
        <div className="animate-spin rounded-full h-8 w-8 border-2 border-purple-500 border-t-transparent"></div>
      </div>
    );
  }

  if (!isSuperAdmin) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-[#f2f2f7]" style={{ fontFamily: '-apple-system, BlinkMacSystemFont, "SF Pro Display", "SF Pro Text", system-ui, sans-serif' }}>
        <div className="text-center px-6">
          <Shield className="h-12 w-12 text-red-400 mx-auto mb-4" />
          <h1 className="text-[22px] font-bold text-gray-900 mb-2" style={{ letterSpacing: '-0.02em' }}>Access Denied</h1>
          <p className="text-[15px] text-gray-500">You don't have permission to access this page.</p>
        </div>
      </div>
    );
  }

  const getStatusColor = (status: SuperAdminBusinessListStatus, adminAccess?: BusinessSummary['admin_access']) => {
    if (adminAccess?.kind === 'trial') return 'text-blue-700 bg-blue-100';
    switch (status) {
      case 'active':
        return 'text-green-600 bg-green-100';
      case 'past_due':
        return 'text-amber-800 bg-amber-100';
      case 'canceled':
        return 'text-red-700 bg-red-100';
      case 'inactive':
        return 'text-gray-600 bg-gray-100';
      case 'suspended':
        return 'text-red-700 bg-red-100';
      default:
        return 'text-gray-600 bg-gray-100';
    }
  };


  return (
    <div
      className="min-h-screen bg-[#f2f2f7]"
      style={{
        paddingTop: 'env(safe-area-inset-top, 0px)',
        paddingBottom: 'env(safe-area-inset-bottom, 0px)',
        fontFamily: '-apple-system, BlinkMacSystemFont, "SF Pro Display", "SF Pro Text", system-ui, sans-serif'
      }}
    >
      {/* Sticky iOS Header */}
      <div
        className="sticky top-0 z-30 px-4 pt-4 pb-3"
        style={{
          background: 'rgba(242, 242, 247, 0.85)',
          backdropFilter: 'blur(20px) saturate(180%)',
          WebkitBackdropFilter: 'blur(20px) saturate(180%)'
        }}
      >
        <div className="flex items-center justify-between mb-3">
          <h1 className="text-[28px] font-bold text-gray-900" style={{ letterSpacing: '-0.02em' }}>Admin</h1>
          <button 
            onClick={() => {
              if (activeTab === 'dashboard') loadSalesDashboard(salesDateRange);
              else if (activeTab === 'businesses') loadDashboardData();
              else if (activeTab === 'invoices') {
                loadStripeInvoices();
                loadBillingMetrics();
              }
              else if (activeTab === 'activity') loadActivityLogs();
              else if (activeTab === 'errors') loadErrorLogs();
              else if (activeTab === 'push') loadPushLogs();
              else if (activeTab === 'requests') loadRequests();
              else if (activeTab === 'bugs') loadBugReports();
            }}
            className="h-9 w-9 flex items-center justify-center text-gray-500 hover:text-gray-700 bg-white/60 rounded-full active:scale-95 transition-all touch-manipulation"
            style={{ WebkitTapHighlightColor: 'transparent' }}
          >
            <RefreshCw className="h-4.5 w-4.5" />
          </button>
        </div>

        {/* iOS Segmented Control */}
        <div className="bg-black/[0.06] rounded-[9px] p-[2px] flex">
          {([
            { key: 'dashboard', label: 'Dashboard', icon: BarChart3 },
            { key: 'businesses', label: 'Businesses', icon: Building2 },
            { key: 'signups', label: 'Signups', icon: TrendingUp },
            { key: 'signup-flows', label: 'Sign up flow', icon: Route },
            { key: 'invoices', label: 'Invoices', icon: Receipt },
            { key: 'partners', label: 'Partners', icon: Handshake },
            { key: 'activity', label: 'Activity', icon: Activity },
            { key: 'errors', label: 'Errors', icon: AlertTriangle },
            { key: 'push', label: 'Push', icon: Bell },
            { key: 'requests', label: 'Requests', icon: Inbox },
            { key: 'bugs', label: 'Bugs', icon: Bug },
          ] as const).map(tab => (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key)}
              className={`relative flex-1 flex items-center justify-center gap-1 py-[7px] text-[13px] font-semibold rounded-[7px] transition-all duration-200 touch-manipulation ${
                activeTab === tab.key
                  ? 'bg-white text-gray-900 shadow-[0_1px_3px_rgba(0,0,0,0.08),0_1px_1px_rgba(0,0,0,0.04)]'
                  : 'text-gray-500'
              }`}
              style={{ WebkitTapHighlightColor: 'transparent' }}
            >
              <tab.icon className="h-3.5 w-3.5" />
              <span className="hidden sm:inline">{tab.label}</span>
              {tab.key === 'bugs' && liveBugCount > 0 && (
                <span className="absolute -top-1 -right-1 min-w-[18px] h-[18px] flex items-center justify-center rounded-full bg-red-500 text-white text-[11px] font-bold px-1 leading-none shadow-sm">
                  {liveBugCount > 99 ? '99+' : liveBugCount}
                </span>
              )}
              {tab.key === 'requests' && liveRequestCount > 0 && (
                <span className="absolute -top-1 -right-1 min-w-[18px] h-[18px] flex items-center justify-center rounded-full bg-amber-500 text-white text-[11px] font-bold px-1 leading-none shadow-sm">
                  {liveRequestCount > 99 ? '99+' : liveRequestCount}
                </span>
              )}
            </button>
          ))}
        </div>
      </div>


      {activeTab === 'dashboard' ? (
        /* Sales Dashboard Tab */
        <div className="px-4 pb-6">
          {/* Date range selector */}
          <div className="flex items-center gap-2 mb-5">
            <Calendar className="h-4 w-4 text-gray-400" />
            {(['this_month', 'last_month', 'this_year', 'last_year'] as SalesDateRange[]).map(range => {
              const labels: Record<SalesDateRange, string> = { this_month: 'This Month', last_month: 'Last Month', this_year: 'This Year', last_year: 'Last Year' };
              return (
                <button
                  key={range}
                  onClick={() => setSalesDateRange(range)}
                  className={`px-3 py-1.5 text-[13px] font-semibold rounded-lg transition-all touch-manipulation ${
                    salesDateRange === range
                      ? 'bg-blue-600 text-white shadow-sm'
                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}
                  style={{ WebkitTapHighlightColor: 'transparent' }}
                >
                  {labels[range]}
                </button>
              );
            })}
          </div>

          {/* Chart */}
          <div className="bg-white rounded-2xl border border-gray-200 shadow-sm p-4 sm:p-6 mb-5">
            <h3 className="text-[15px] font-semibold text-gray-900 mb-4">Sales by Business</h3>
            {salesLoading ? (
              <div className="flex items-center justify-center h-[350px]">
                <div className="w-6 h-6 border-2 border-blue-500/30 border-t-blue-500 rounded-full animate-spin" />
              </div>
            ) : salesChartData && salesChartData.datasets.length > 0 ? (
              <div style={{ height: '350px' }}>
                <Line
                  data={salesChartData}
                  options={{
                    responsive: true,
                    maintainAspectRatio: false,
                    interaction: { mode: 'index' as const, intersect: false },
                    plugins: {
                      legend: { display: true, position: 'bottom' as const, labels: { usePointStyle: true, pointStyle: 'circle', padding: 16, font: { size: 12 } } },
                      tooltip: {
                        backgroundColor: 'rgba(17,24,39,0.95)',
                        titleFont: { size: 13, weight: 'bold' as const },
                        bodyFont: { size: 12 },
                        padding: 12,
                        cornerRadius: 10,
                        callbacks: {
                          label: (ctx: any) => {
                            const val = ctx.parsed.y || 0;
                            return ` ${ctx.dataset.label}: $${val.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
                          },
                        },
                      },
                    },
                    scales: {
                      x: { grid: { display: false }, ticks: { font: { size: 11 }, color: '#9CA3AF' } },
                      y: {
                        beginAtZero: true,
                        grid: { color: 'rgba(0,0,0,0.04)' },
                        ticks: {
                          font: { size: 11 },
                          color: '#9CA3AF',
                          callback: (value: any) => '$' + Number(value).toLocaleString(),
                        },
                      },
                    },
                  }}
                />
              </div>
            ) : (
              <div className="flex items-center justify-center h-[350px] text-gray-400 text-[14px]">
                No sales data for this period
              </div>
            )}
          </div>

          {/* Totals breakdown */}
          {salesTotals.length > 0 && (
            <div className="bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden">
              <div className="px-4 sm:px-6 py-3 border-b border-gray-100 bg-gray-50/50">
                <h3 className="text-[13px] font-semibold text-gray-500 uppercase tracking-wider">Totals</h3>
              </div>
              <div className="divide-y divide-gray-100">
                {salesTotals.map((biz, i) => (
                  <div key={i} className="flex items-center justify-between px-4 sm:px-6 py-3">
                    <div className="flex items-center gap-2.5">
                      <span
                        className="w-3 h-3 rounded-full flex-shrink-0"
                        style={{ backgroundColor: CHART_COLORS[i % CHART_COLORS.length] }}
                      />
                      <span className="text-[14px] text-gray-800 font-medium">{biz.name}</span>
                    </div>
                    <span className="text-[14px] font-semibold text-gray-900">
                      ${biz.total.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}
                    </span>
                  </div>
                ))}
                <div className="flex items-center justify-between px-4 sm:px-6 py-3 bg-gray-50/80">
                  <span className="text-[14px] font-semibold text-gray-700">Platform Total</span>
                  <span className="text-[15px] font-bold text-gray-900">
                    ${salesTotals.reduce((s, b) => s + b.total, 0).toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}
                  </span>
                </div>
              </div>
            </div>
          )}
        </div>
      ) : activeTab === 'bugs' ? (
        /* Bugs Tab */
        <div className="px-4 pb-6">
          <p className="text-[13px] text-gray-500 mb-4">User-reported bugs · fixed bugs are hidden</p>
          {bugsLoading ? (
            <div className="flex items-center justify-center py-20">
              <div className="animate-spin rounded-full h-8 w-8 border-2 border-red-500 border-t-transparent"></div>
            </div>
          ) : bugReports.length === 0 ? (
            <div className="bg-white rounded-[14px] p-8 text-center" style={{ boxShadow: '0 0 0 0.5px rgba(0,0,0,0.04), 0 1px 2px rgba(0,0,0,0.04)' }}>
              <Bug className="h-10 w-10 text-gray-300 mx-auto mb-3" />
              <p className="text-[15px] font-medium text-gray-500">No open bug reports</p>
              <p className="text-[13px] text-gray-400 mt-1">All clear!</p>
            </div>
          ) : (
            <div className="space-y-3">
              {bugReports.map((bug: any) => (
                <div key={bug.id} className="bg-white rounded-[14px] overflow-hidden" style={{ boxShadow: '0 0 0 0.5px rgba(0,0,0,0.04), 0 1px 2px rgba(0,0,0,0.04)' }}>
                  <div className="p-4">
                    <div className="flex items-center gap-2 flex-wrap mb-2">
                      <span className={`inline-flex px-2 py-0.5 text-[11px] font-bold rounded-full uppercase tracking-wide ${
                        bug.status === 'new' ? 'bg-red-100 text-red-700' :
                        bug.status === 'investigating' ? 'bg-amber-100 text-amber-700' :
                        'bg-gray-100 text-gray-600'
                      }`}>
                        {bug.status === 'new' ? 'New' : bug.status === 'investigating' ? 'Investigating' : bug.status}
                      </span>
                      <span className="text-[12px] text-gray-400">
                        {new Date(bug.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} at {new Date(bug.created_at).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })}
                      </span>
                    </div>
                    <p className="text-[13px] text-gray-500 mb-2">
                      {bug.user_email}{bug.business_name ? ` · ${bug.business_name}` : ''}
                    </p>
                    <p className="text-[15px] text-gray-900 whitespace-pre-wrap leading-relaxed">{bug.description}</p>

                    {bug.photo_url && (
                      <button
                        onClick={() => openImageViewer({ images: [{ src: bug.photo_url }], index: 0 })}
                        className="mt-3 block rounded-[10px] overflow-hidden border border-gray-200/60 active:opacity-70 transition-opacity touch-manipulation"
                        style={{ WebkitTapHighlightColor: 'transparent' }}
                      >
                        <img src={bug.photo_url} alt="Bug screenshot" className="max-h-48 object-contain bg-gray-50" />
                      </button>
                    )}

                    {bug.admin_reply && (
                      <div className="mt-3 rounded-[10px] bg-blue-50 border border-blue-100 px-3 py-2">
                        <p className="text-[11px] font-bold uppercase tracking-wide text-blue-600 mb-0.5">Your reply</p>
                        <p className="text-[13px] text-gray-800 whitespace-pre-wrap">{bug.admin_reply}</p>
                      </div>
                    )}

                    {ticketReplyOpenId === bug.id ? (
                      <div className="mt-3 pt-3 border-t border-gray-100 space-y-2">
                        <textarea
                          value={ticketReplyDraft}
                          onChange={(e) => setTicketReplyDraft(e.target.value)}
                          rows={3}
                          placeholder="Write a reply — the reporter sees it in their Help Centre and gets a push notification…"
                          className="w-full px-3 py-2 border border-gray-300 rounded-[10px] text-[14px] focus:outline-none focus:ring-2 focus:ring-red-500/30"
                        />
                        <div className="flex gap-2">
                          <button
                            onClick={() => sendBugReply(bug)}
                            disabled={ticketReplyBusy || !ticketReplyDraft.trim()}
                            className="px-3.5 py-1.5 text-[13px] font-semibold bg-blue-600 text-white rounded-full disabled:opacity-50 active:scale-95 transition-all touch-manipulation"
                          >
                            {ticketReplyBusy ? 'Sending…' : 'Send reply'}
                          </button>
                          <button
                            onClick={() => { setTicketReplyOpenId(null); setTicketReplyDraft(''); }}
                            disabled={ticketReplyBusy}
                            className="px-3.5 py-1.5 text-[13px] font-semibold bg-gray-100 text-gray-600 rounded-full active:scale-95 transition-all touch-manipulation"
                          >
                            Cancel
                          </button>
                        </div>
                      </div>
                    ) : (
                      <div className="flex items-center gap-2 mt-3 pt-3 border-t border-gray-100">
                        {bug.status === 'new' && (
                          <button
                            onClick={() => updateBugStatus(bug.id, 'investigating')}
                            className="px-3.5 py-1.5 text-[13px] font-semibold bg-amber-50 text-amber-700 rounded-full active:scale-95 transition-all touch-manipulation"
                            style={{ WebkitTapHighlightColor: 'transparent' }}
                          >
                            Investigating
                          </button>
                        )}
                        <button
                          onClick={() => { setTicketReplyOpenId(bug.id); setTicketReplyDraft(bug.admin_reply || ''); }}
                          className="px-3.5 py-1.5 text-[13px] font-semibold bg-blue-50 text-blue-700 rounded-full active:scale-95 transition-all touch-manipulation"
                          style={{ WebkitTapHighlightColor: 'transparent' }}
                        >
                          {bug.admin_reply ? 'Edit reply' : 'Reply'}
                        </button>
                        {bug.status !== 'fixed' && (
                          <button
                            onClick={() => updateBugStatus(bug.id, 'fixed')}
                            className="px-3.5 py-1.5 text-[13px] font-semibold bg-green-50 text-green-700 rounded-full active:scale-95 transition-all touch-manipulation"
                            style={{ WebkitTapHighlightColor: 'transparent' }}
                          >
                            Mark Fixed
                          </button>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}

        </div>
      ) : activeTab === 'requests' ? (
        /* Support Requests Tab (Help Centre submissions) */
        <div className="px-4 pb-6">
          <p className="text-[13px] text-gray-500 mb-4">Support requests from the Help Centre · closed requests are hidden</p>
          {requestsLoading ? (
            <div className="flex items-center justify-center py-20">
              <div className="animate-spin rounded-full h-8 w-8 border-2 border-purple-500 border-t-transparent"></div>
            </div>
          ) : requests.length === 0 ? (
            <div className="bg-white rounded-[14px] p-8 text-center" style={{ boxShadow: '0 0 0 0.5px rgba(0,0,0,0.04), 0 1px 2px rgba(0,0,0,0.04)' }}>
              <Inbox className="h-10 w-10 text-gray-300 mx-auto mb-3" />
              <p className="text-[15px] font-medium text-gray-500">No open support requests</p>
              <p className="text-[13px] text-gray-400 mt-1">All caught up!</p>
            </div>
          ) : (
            <div className="space-y-3">
              {requests.map((req: any) => (
                <div key={req.id} className="bg-white rounded-[14px] overflow-hidden" style={{ boxShadow: '0 0 0 0.5px rgba(0,0,0,0.04), 0 1px 2px rgba(0,0,0,0.04)' }}>
                  <div className="p-4">
                    <div className="flex items-center gap-2 flex-wrap mb-2">
                      <span className={`inline-flex px-2 py-0.5 text-[11px] font-bold rounded-full uppercase tracking-wide ${
                        req.status === 'open' ? 'bg-amber-100 text-amber-700' :
                        req.status === 'replied' ? 'bg-blue-100 text-blue-700' :
                        'bg-gray-100 text-gray-600'
                      }`}>
                        {req.status === 'open' ? 'Open' : req.status === 'replied' ? 'Replied' : req.status}
                      </span>
                      <span className="text-[12px] text-gray-400">
                        {new Date(req.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} at {new Date(req.created_at).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })}
                      </span>
                    </div>
                    <p className="text-[13px] text-gray-500 mb-1">
                      {req.ticket_number ? <span className="font-semibold text-gray-700">{req.ticket_number} · </span> : null}
                      {req.user_email}{req.business_name ? ` · ${req.business_name}` : ''}
                    </p>
                    <p className="text-[15px] font-semibold text-gray-900">{req.subject}</p>
                    <p className="text-[14px] text-gray-700 whitespace-pre-wrap leading-relaxed mt-1">{req.description}</p>

                    {req.photo_url && (
                      <button
                        onClick={() => openImageViewer({ images: [{ src: req.photo_url }], index: 0 })}
                        className="mt-3 block rounded-[10px] overflow-hidden border border-gray-200/60 active:opacity-70 transition-opacity touch-manipulation"
                        style={{ WebkitTapHighlightColor: 'transparent' }}
                      >
                        <img src={req.photo_url} alt="Screenshot" className="max-h-48 object-contain bg-gray-50" />
                      </button>
                    )}

                    {/* Conversation thread */}
                    {(req.messages || []).length > 0 && (
                      <div className="mt-3 space-y-2">
                        {(req.messages || []).map((m: any) => (
                          <div
                            key={m.id}
                            className={`rounded-[10px] px-3 py-2 ${
                              m.sender === 'admin'
                                ? 'bg-blue-50 border border-blue-100 ml-6'
                                : 'bg-gray-50 border border-gray-100 mr-6'
                            }`}
                          >
                            <p className={`text-[11px] font-bold uppercase tracking-wide mb-0.5 ${
                              m.sender === 'admin' ? 'text-blue-600' : 'text-gray-400'
                            }`}>
                              {m.sender === 'admin' ? 'You' : 'User'}
                              {' · '}
                              {new Date(m.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} at {new Date(m.created_at).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })}
                            </p>
                            <p className="text-[13px] text-gray-800 whitespace-pre-wrap">{m.body}</p>
                          </div>
                        ))}
                      </div>
                    )}

                    {ticketReplyOpenId === req.id ? (
                      <div className="mt-3 pt-3 border-t border-gray-100 space-y-2">
                        <textarea
                          value={ticketReplyDraft}
                          onChange={(e) => setTicketReplyDraft(e.target.value)}
                          rows={3}
                          placeholder="Write a reply — the user sees it in their Help Centre and gets a push notification…"
                          className="w-full px-3 py-2 border border-gray-300 rounded-[10px] text-[14px] focus:outline-none focus:ring-2 focus:ring-purple-500/30"
                        />
                        <div className="flex gap-2">
                          <button
                            onClick={() => sendSupportReply(req)}
                            disabled={ticketReplyBusy || !ticketReplyDraft.trim()}
                            className="px-3.5 py-1.5 text-[13px] font-semibold bg-purple-600 text-white rounded-full disabled:opacity-50 active:scale-95 transition-all touch-manipulation"
                          >
                            {ticketReplyBusy ? 'Sending…' : 'Send reply'}
                          </button>
                          <button
                            onClick={() => { setTicketReplyOpenId(null); setTicketReplyDraft(''); }}
                            disabled={ticketReplyBusy}
                            className="px-3.5 py-1.5 text-[13px] font-semibold bg-gray-100 text-gray-600 rounded-full active:scale-95 transition-all touch-manipulation"
                          >
                            Cancel
                          </button>
                        </div>
                      </div>
                    ) : (
                      <div className="flex items-center gap-2 mt-3 pt-3 border-t border-gray-100">
                        <button
                          onClick={() => { setTicketReplyOpenId(req.id); setTicketReplyDraft(''); }}
                          className="px-3.5 py-1.5 text-[13px] font-semibold bg-blue-50 text-blue-700 rounded-full active:scale-95 transition-all touch-manipulation"
                          style={{ WebkitTapHighlightColor: 'transparent' }}
                        >
                          Reply
                        </button>
                        <button
                          onClick={() => closeSupportRequest(req)}
                          className="px-3.5 py-1.5 text-[13px] font-semibold bg-gray-100 text-gray-600 rounded-full active:scale-95 transition-all touch-manipulation"
                          style={{ WebkitTapHighlightColor: 'transparent' }}
                        >
                          Close
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      ) : activeTab === 'push' ? (
        /* Push Notification Delivery Logs Tab */
        <div className="px-4 pb-6">
          {/* One-time Twilio inbound backfill — isolated test */}
          <div className="bg-white rounded-[12px] p-4 mb-4" style={{ boxShadow: '0 0 0 0.5px rgba(0,0,0,0.04), 0 1px 2px rgba(0,0,0,0.04)' }}>
            <div className="text-[14px] font-semibold text-gray-900">Twilio inbound backfill (all businesses)</div>
            <div className="text-[12px] text-gray-500 mt-0.5 mb-3">
              All Twilio-enabled businesses · window Jun 7 → Jun 10 GMT · inbound only · dedup by message SID
            </div>
            <button
              type="button"
              onClick={runSmsBackfillTest}
              disabled={smsBackfillLoading}
              className="inline-flex items-center gap-2 px-3 py-2 rounded-[10px] bg-purple-600 text-white text-[13px] font-medium hover:bg-purple-700 disabled:opacity-60"
            >
              {smsBackfillLoading ? 'Running…' : 'Run backfill (all businesses)'}
            </button>
            {smsBackfillResult && (
              <pre className="mt-3 p-3 bg-gray-50 rounded-[10px] text-[11px] text-gray-700 overflow-auto max-h-72 whitespace-pre-wrap">{smsBackfillResult}</pre>
            )}
          </div>

          <p className="text-[13px] text-gray-500 mb-4">Push delivery attempts — most recent 500 (newest first)</p>

          {/* Search */}
          <div className="relative mb-3">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
            <input
              type="text"
              placeholder="Search by event, user, business, error..."
              value={pushSearchTerm}
              onChange={(e) => setPushSearchTerm(e.target.value)}
              className="w-full pl-9 pr-4 py-2.5 bg-white rounded-[12px] text-[15px] text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-purple-500/30 transition-all"
              style={{ boxShadow: '0 0 0 0.5px rgba(0,0,0,0.04), 0 1px 2px rgba(0,0,0,0.04)', fontSize: '16px' }}
            />
          </div>

          {/* Status filter */}
          <div className="mb-4">
            <select
              value={pushStatusFilter}
              onChange={(e) => setPushStatusFilter(e.target.value as 'all' | 'success' | 'failed')}
              className="w-full px-3 py-2.5 bg-white rounded-[12px] text-[13px] text-gray-700 focus:outline-none focus:ring-2 focus:ring-purple-500/30 appearance-none"
              style={{ boxShadow: '0 0 0 0.5px rgba(0,0,0,0.04), 0 1px 2px rgba(0,0,0,0.04)' }}
            >
              <option value="all">All Attempts</option>
              <option value="success">Delivered (success)</option>
              <option value="failed">Failed</option>
            </select>
          </div>

          {/* Stats */}
          <div className="grid grid-cols-3 gap-2 mb-4">
            {(() => {
              const total = pushLogs.length;
              const sent = pushLogs.filter((l: any) => l.success).length;
              const failed = total - sent;
              const rate = total ? Math.round((sent / total) * 100) : 0;
              return [
                { label: 'Attempts', value: total, color: 'text-gray-900' },
                { label: 'Delivered', value: sent, color: 'text-green-600' },
                { label: `Failed (${rate}% ok)`, value: failed, color: 'text-red-600' },
              ];
            })().map(stat => (
              <div key={stat.label} className="bg-white rounded-[12px] p-3 text-center" style={{ boxShadow: '0 0 0 0.5px rgba(0,0,0,0.04), 0 1px 2px rgba(0,0,0,0.04)' }}>
                <div className={`text-xl font-bold ${stat.color}`}>{stat.value}</div>
                <div className="text-[11px] text-gray-500 font-medium">{stat.label}</div>
              </div>
            ))}
          </div>

          {/* List */}
          {pushLogsLoading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-2 border-purple-500 border-t-transparent"></div>
            </div>
          ) : (() => {
            const term = pushSearchTerm.toLowerCase();
            const filtered = pushLogs.filter((l: any) => {
              if (!term) return true;
              return (
                (l.event_type || '').toLowerCase().includes(term) ||
                (l.event_id || '').toLowerCase().includes(term) ||
                (l.notification_title || '').toLowerCase().includes(term) ||
                (l.notification_body || '').toLowerCase().includes(term) ||
                (l.error_detail || '').toLowerCase().includes(term) ||
                (l.apns_reason || '').toLowerCase().includes(term) ||
                String(l.target_user_id || '').toLowerCase().includes(term) ||
                String(l.business_id || '').toLowerCase().includes(term) ||
                (l.platform || '').toLowerCase().includes(term)
              );
            });

            if (filtered.length === 0) {
              return (
                <div className="bg-white rounded-[12px] p-8 text-center text-[13px] text-gray-500" style={{ boxShadow: '0 0 0 0.5px rgba(0,0,0,0.04), 0 1px 2px rgba(0,0,0,0.04)' }}>
                  No push logs yet. They appear here as notifications are sent.
                </div>
              );
            }

            return (
              <div className="space-y-2">
                {filtered.map((l: any) => (
                  <div key={l.id} className="bg-white rounded-[12px] p-3" style={{ boxShadow: '0 0 0 0.5px rgba(0,0,0,0.04), 0 1px 2px rgba(0,0,0,0.04)' }}>
                    <div className="flex items-start justify-between gap-2">
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-semibold ${l.success ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                            {l.success ? 'Delivered' : 'Failed'}
                          </span>
                          <span className="text-[12px] font-semibold text-gray-900 capitalize">{l.event_type || 'generic'}</span>
                          {l.platform && <span className="text-[11px] text-gray-500">{l.platform}</span>}
                          {typeof l.status_code === 'number' && <span className="text-[11px] text-gray-400">HTTP {l.status_code}</span>}
                        </div>
                        {l.notification_title && (
                          <p className="text-[13px] text-gray-800 mt-1 truncate">
                            {l.notification_title}{l.notification_body ? ` — ${l.notification_body}` : ''}
                          </p>
                        )}
                        {(l.apns_reason || l.error_detail) && (
                          <p className="text-[12px] text-red-600 mt-1 break-words">{l.apns_reason || l.error_detail}</p>
                        )}
                        <div className="text-[11px] text-gray-400 mt-1 space-x-2">
                          {l.target_user_id && <span>user {String(l.target_user_id).slice(0, 8)}</span>}
                          {l.business_id && <span>biz {String(l.business_id).slice(0, 8)}</span>}
                          {l.device_token_masked && <span>{l.device_token_masked}</span>}
                          {typeof l.badge_count === 'number' && <span>badge {l.badge_count}</span>}
                        </div>
                      </div>
                      <span className="text-[11px] text-gray-400 whitespace-nowrap">{new Date(l.created_at).toLocaleString()}</span>
                    </div>
                  </div>
                ))}
              </div>
            );
          })()}
        </div>
      ) : activeTab === 'errors' ? (
        /* Error Logs Tab */
        <div className="px-4 pb-6">
          <p className="text-[13px] text-gray-500 mb-4">Errors encountered by users</p>

          {/* iOS-style search + filters */}
          <div className="relative mb-3">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
            <input
              type="text"
              placeholder="Search errors..."
              value={errorSearchTerm}
              onChange={(e) => setErrorSearchTerm(e.target.value)}
              className="w-full pl-9 pr-4 py-2.5 bg-white rounded-[12px] text-[15px] text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-purple-500/30 transition-all"
              style={{ boxShadow: '0 0 0 0.5px rgba(0,0,0,0.04), 0 1px 2px rgba(0,0,0,0.04)', fontSize: '16px' }}
            />
          </div>

          <div className="grid grid-cols-2 gap-2 mb-4">
            <select
              value={errorStatusFilter}
              onChange={(e) => setErrorStatusFilter(e.target.value)}
              className="w-full px-3 py-2.5 bg-white rounded-[12px] text-[13px] text-gray-700 focus:outline-none focus:ring-2 focus:ring-purple-500/30 appearance-none"
              style={{ boxShadow: '0 0 0 0.5px rgba(0,0,0,0.04), 0 1px 2px rgba(0,0,0,0.04)' }}
            >
              <option value="all">All Statuses</option>
              <option value="new">New</option>
              <option value="investigating">Investigating</option>
              <option value="resolved">Resolved</option>
              <option value="ignored">Ignored</option>
            </select>
            <select
              value={errorTypeFilter}
              onChange={(e) => setErrorTypeFilter(e.target.value)}
              className="w-full px-3 py-2.5 bg-white rounded-[12px] text-[13px] text-gray-700 focus:outline-none focus:ring-2 focus:ring-purple-500/30 appearance-none"
              style={{ boxShadow: '0 0 0 0.5px rgba(0,0,0,0.04), 0 1px 2px rgba(0,0,0,0.04)' }}
            >
              <option value="all">All Types</option>
              <option value="react_error">React</option>
              <option value="js_error">JavaScript</option>
              <option value="api_error">API</option>
              <option value="network_error">Network</option>
              <option value="unhandled_promise">Promise</option>
              <option value="service_worker_error">SW</option>
            </select>
          </div>

          {/* Stats row */}
          <div className="grid grid-cols-4 gap-2 mb-4">
            {[
              { label: 'Total', value: errorLogs.length, color: 'text-gray-900' },
              { label: 'New', value: errorLogs.filter((e: any) => e.status === 'new').length, color: 'text-red-600' },
              { label: 'Open', value: errorLogs.filter((e: any) => e.status === 'investigating').length, color: 'text-amber-600' },
              { label: 'Fixed', value: errorLogs.filter((e: any) => e.status === 'resolved').length, color: 'text-green-600' },
            ].map(stat => (
              <div key={stat.label} className="bg-white rounded-[12px] p-3 text-center" style={{ boxShadow: '0 0 0 0.5px rgba(0,0,0,0.04), 0 1px 2px rgba(0,0,0,0.04)' }}>
                <div className={`text-xl font-bold ${stat.color}`}>{stat.value}</div>
                <div className="text-[11px] text-gray-500 font-medium">{stat.label}</div>
              </div>
            ))}
          </div>

          {/* Error List */}
          {errorLogsLoading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-2 border-purple-500 border-t-transparent"></div>
            </div>
          ) : (() => {
            const filteredErrors = errorLogs.filter((error: any) => {
              if (errorSearchTerm) {
                const searchLower = errorSearchTerm.toLowerCase();
                return (
                  error.error_message.toLowerCase().includes(searchLower) ||
                  error.page_path?.toLowerCase().includes(searchLower) ||
                  error.component_name?.toLowerCase().includes(searchLower) ||
                  error.error_name?.toLowerCase().includes(searchLower)
                );
              }
              return true;
            });

            const getErrorStatusColor = (status: string) => {
              switch (status) {
                case 'new': return 'bg-red-100 text-red-700';
                case 'investigating': return 'bg-amber-100 text-amber-700';
                case 'resolved': return 'bg-green-100 text-green-700';
                case 'ignored': return 'bg-gray-100 text-gray-600';
                default: return 'bg-gray-100 text-gray-600';
              }
            };

            const getTypeColor = (type: string) => {
              switch (type) {
                case 'react_error': return 'bg-purple-100 text-purple-700';
                case 'js_error': return 'bg-blue-100 text-blue-700';
                case 'api_error': return 'bg-orange-100 text-orange-700';
                case 'network_error': return 'bg-pink-100 text-pink-700';
                case 'unhandled_promise': return 'bg-indigo-100 text-indigo-700';
                case 'service_worker_error': return 'bg-teal-100 text-teal-700';
                default: return 'bg-gray-100 text-gray-600';
              }
            };

            return filteredErrors.length === 0 ? (
              <div className="bg-white rounded-[14px] p-10 text-center" style={{ boxShadow: '0 0 0 0.5px rgba(0,0,0,0.04), 0 1px 2px rgba(0,0,0,0.04)' }}>
                <AlertTriangle className="h-10 w-10 text-gray-300 mx-auto mb-3" />
                <p className="text-[15px] text-gray-500">No errors found</p>
              </div>
            ) : (
              <div className="space-y-3">
                {filteredErrors.map((error: any) => (
                  <div key={error.id} className="bg-white rounded-[14px] overflow-hidden" style={{ boxShadow: '0 0 0 0.5px rgba(0,0,0,0.04), 0 1px 2px rgba(0,0,0,0.04)' }}>
                    <button
                      onClick={() => toggleErrorExpand(error.id)}
                      className="w-full text-left p-4 active:bg-gray-50/50 transition-colors touch-manipulation"
                      style={{ WebkitTapHighlightColor: 'transparent' }}
                    >
                      <div className="flex items-center gap-1.5 flex-wrap mb-2">
                        <span className={`px-2 py-0.5 rounded-full text-[11px] font-bold uppercase tracking-wide ${getTypeColor(error.error_type)}`}>
                          {error.error_type.replace('_', ' ')}
                        </span>
                        <span className={`px-2 py-0.5 rounded-full text-[11px] font-bold uppercase tracking-wide ${getErrorStatusColor(error.status)}`}>
                          {error.status}
                        </span>
                        {error.occurrence_count > 1 && (
                          <span className="px-2 py-0.5 rounded-full text-[11px] font-bold bg-blue-100 text-blue-700">
                            {error.occurrence_count}x
                          </span>
                        )}
                      </div>
                      <p className="text-[15px] font-medium text-gray-900 leading-snug line-clamp-2">
                        {error.error_message.substring(0, 200)}{error.error_message.length > 200 && '...'}
                      </p>
                      <div className="flex items-center gap-2 flex-wrap mt-2 text-[12px] text-gray-400">
                        {error.page_path && <span>{error.page_path}</span>}
                        {error.component_name && <><span>·</span><span>{error.component_name}</span></>}
                        <span>·</span>
                        <span>{new Date(error.first_seen_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}</span>
                      </div>
                    </button>

                    {expandedErrors.has(error.id) && (
                      <div className="px-4 pb-4 border-t border-gray-100">
                        {/* Action buttons */}
                        <div className="flex items-center gap-2 py-3">
                          {error.status === 'new' && (
                            <button onClick={() => updateErrorStatus(error.id, 'investigating')} className="px-3.5 py-1.5 text-[13px] font-semibold bg-amber-50 text-amber-700 rounded-full active:scale-95 transition-all touch-manipulation" style={{ WebkitTapHighlightColor: 'transparent' }}>Investigating</button>
                          )}
                          {error.status !== 'resolved' && (
                            <button onClick={() => updateErrorStatus(error.id, 'resolved')} className="px-3.5 py-1.5 text-[13px] font-semibold bg-green-50 text-green-700 rounded-full active:scale-95 transition-all touch-manipulation" style={{ WebkitTapHighlightColor: 'transparent' }}>Resolve</button>
                          )}
                          {error.status !== 'ignored' && (
                            <button onClick={() => updateErrorStatus(error.id, 'ignored')} className="px-3.5 py-1.5 text-[13px] font-semibold bg-gray-100 text-gray-600 rounded-full active:scale-95 transition-all touch-manipulation" style={{ WebkitTapHighlightColor: 'transparent' }}>Ignore</button>
                          )}
                        </div>

                        {error.error_stack && (
                          <div className="mb-3">
                            <div className="text-[13px] font-semibold text-gray-700 mb-1">Stack Trace</div>
                            <pre className="bg-[#f2f2f7] p-3 rounded-[10px] text-[11px] overflow-auto max-h-60 font-mono text-gray-700">{error.error_stack}</pre>
                          </div>
                        )}
                        {error.component_stack && (
                          <div className="mb-3">
                            <div className="text-[13px] font-semibold text-gray-700 mb-1">Component Stack</div>
                            <pre className="bg-[#f2f2f7] p-3 rounded-[10px] text-[11px] overflow-auto max-h-60 font-mono text-gray-700">{error.component_stack}</pre>
                          </div>
                        )}
                        {error.metadata && (
                          <div className="mb-3">
                            <div className="text-[13px] font-semibold text-gray-700 mb-1">Metadata</div>
                            <pre className="bg-[#f2f2f7] p-3 rounded-[10px] text-[11px] overflow-auto max-h-60 font-mono text-gray-700">{JSON.stringify(error.metadata, null, 2)}</pre>
                          </div>
                        )}
                        <div className="rounded-[10px] overflow-hidden" style={{ background: 'rgba(118,118,128,0.08)' }}>
                          {[
                            { label: 'Error Name', val: error.error_name },
                            { label: 'URL', val: error.url },
                            error.line_number && { label: 'Line', val: `${error.line_number}:${error.column_number}` },
                            error.source_file && { label: 'Source', val: error.source_file },
                            { label: 'Device', val: error.device_type },
                            { label: 'Browser', val: error.browser_name ? `${error.browser_name} ${error.browser_version || ''}` : null },
                          ].filter(Boolean).map((item: any, idx) => (
                            <div key={idx} className={`flex items-center justify-between px-3 py-2.5 ${idx > 0 ? 'border-t border-gray-200/40' : ''}`}>
                              <span className="text-[13px] text-gray-500">{item.label}</span>
                              <span className="text-[13px] font-medium text-gray-900 text-right max-w-[60%] truncate">{item.val || 'N/A'}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            );
          })()}
        </div>
      ) : activeTab === 'activity' ? (
        /* Activity Logs Tab */
        <div className="px-4 pb-6">
          {/* iOS-style search */}
          <div className="relative mb-3">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
            <input
              type="text"
              placeholder="Search activity..."
              value={activitySearch}
              onChange={(e) => setActivitySearch(e.target.value)}
              className="w-full pl-9 pr-4 py-2.5 bg-white rounded-[12px] text-[15px] text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-purple-500/30 transition-all"
              style={{ boxShadow: '0 0 0 0.5px rgba(0,0,0,0.04), 0 1px 2px rgba(0,0,0,0.04)', fontSize: '16px' }}
            />
          </div>

          {/* Compact filter row */}
          <div className="grid grid-cols-2 gap-2 mb-3">
            <select
              value={activityFilter.activityType || ''}
              onChange={(e) => setActivityFilter({ ...activityFilter, activityType: e.target.value || undefined })}
              className="w-full px-3 py-2.5 bg-white rounded-[12px] text-[13px] text-gray-700 focus:outline-none focus:ring-2 focus:ring-purple-500/30 appearance-none"
              style={{ boxShadow: '0 0 0 0.5px rgba(0,0,0,0.04), 0 1px 2px rgba(0,0,0,0.04)' }}
            >
              <option value="">All Types</option>
              <option value="login">Login</option>
              <option value="logout">Logout</option>
              <option value="app_start">App Start</option>
              <option value="page_visit">Page Visit</option>
              <option value="action">Action</option>
              <option value="search">Search</option>
              <option value="error">Error</option>
            </select>
            <select
              value={activityFilter.activityCategory || ''}
              onChange={(e) => setActivityFilter({ ...activityFilter, activityCategory: e.target.value || undefined })}
              className="w-full px-3 py-2.5 bg-white rounded-[12px] text-[13px] text-gray-700 focus:outline-none focus:ring-2 focus:ring-purple-500/30 appearance-none"
              style={{ boxShadow: '0 0 0 0.5px rgba(0,0,0,0.04), 0 1px 2px rgba(0,0,0,0.04)' }}
            >
              <option value="">All Categories</option>
              <option value="auth">Auth</option>
              <option value="navigation">Navigation</option>
              <option value="booking">Booking</option>
              <option value="customer">Customer</option>
              <option value="invoice">Invoice</option>
              <option value="proposal">Proposal</option>
              <option value="crm">CRM</option>
              <option value="marketing">Marketing</option>
              <option value="settings">Settings</option>
              <option value="system">System</option>
            </select>
          </div>
          <div className="grid grid-cols-2 gap-2 mb-4">
            <input
              type="date"
              value={activityFilter.dateFrom || ''}
              onChange={(e) => setActivityFilter({ ...activityFilter, dateFrom: e.target.value || undefined })}
              className="w-full px-3 py-2.5 bg-white rounded-[12px] text-[13px] text-gray-700 focus:outline-none focus:ring-2 focus:ring-purple-500/30"
              style={{ boxShadow: '0 0 0 0.5px rgba(0,0,0,0.04), 0 1px 2px rgba(0,0,0,0.04)' }}
              placeholder="From"
            />
            <input
              type="date"
              value={activityFilter.dateTo || ''}
              onChange={(e) => setActivityFilter({ ...activityFilter, dateTo: e.target.value || undefined })}
              className="w-full px-3 py-2.5 bg-white rounded-[12px] text-[13px] text-gray-700 focus:outline-none focus:ring-2 focus:ring-purple-500/30"
              style={{ boxShadow: '0 0 0 0.5px rgba(0,0,0,0.04), 0 1px 2px rgba(0,0,0,0.04)' }}
              placeholder="To"
            />
          </div>

          {/* Activity list */}
          {activityLoading ? (
            <div className="flex items-center justify-center py-20">
              <div className="animate-spin rounded-full h-8 w-8 border-2 border-purple-500 border-t-transparent"></div>
            </div>
          ) : activityLogs.length === 0 ? (
            <div className="bg-white rounded-[14px] p-8 text-center" style={{ boxShadow: '0 0 0 0.5px rgba(0,0,0,0.04), 0 1px 2px rgba(0,0,0,0.04)' }}>
              <Activity className="h-10 w-10 text-gray-300 mx-auto mb-3" />
              <p className="text-[15px] font-medium text-gray-500">No activity logs found</p>
            </div>
          ) : (
            <>
              <div className="bg-white rounded-[14px] overflow-hidden" style={{ boxShadow: '0 0 0 0.5px rgba(0,0,0,0.04), 0 1px 2px rgba(0,0,0,0.04)' }}>
                {activityLogs.map((log, idx) => (
                  <div key={log.id} className={`px-4 py-3 ${idx > 0 ? 'border-t border-gray-100' : ''}`}>
                    <div className="flex items-center gap-2 mb-1">
                      <span className={`inline-flex px-2 py-0.5 text-[11px] font-bold rounded-full uppercase tracking-wide ${
                        log.activity_type === 'login' ? 'bg-green-100 text-green-700' :
                        log.activity_type === 'logout' ? 'bg-red-100 text-red-700' :
                        log.activity_type === 'app_start' ? 'bg-blue-100 text-blue-700' :
                        log.activity_type === 'page_visit' ? 'bg-purple-100 text-purple-700' :
                        log.activity_type === 'error' ? 'bg-red-100 text-red-700' :
                        'bg-gray-100 text-gray-600'
                      }`}>
                        {log.activity_type}
                      </span>
                      {log.activity_category && (
                        <span className="text-[11px] text-gray-400 font-medium uppercase">{log.activity_category}</span>
                      )}
                      <span className="text-[12px] text-gray-400 ml-auto">
                        {new Date(log.created_at).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })}
                      </span>
                    </div>
                    <div className="flex items-center gap-1.5">
                      <p className="text-[15px] font-medium text-gray-900 truncate">
                        {log.page_name || log.action || log.page_path || 'Activity'}
                      </p>
                    </div>
                    <div className="flex items-center gap-1.5 mt-0.5 text-[12px] text-gray-400">
                      <span className="truncate">{log.user?.email || 'Unknown'}</span>
                      {log.business?.name && <><span>·</span><span className="truncate">{log.business.name}</span></>}
                      {log.device_type && <><span>·</span><span>{log.device_type}</span></>}
                    </div>
                  </div>
                ))}
              </div>
              <p className="text-center text-[12px] text-gray-400 mt-3">{activityLogs.length} activity logs</p>
            </>
          )}
        </div>
      ) : activeTab === 'invoices' ? (
        <div className="px-4 pb-6">
          <div className="flex items-center justify-between mb-4 gap-3">
            <p className="text-[13px] text-gray-500">
              Subscriptions, invoices, and AI token purchases
            </p>
            <button
              onClick={() => {
                void handleSyncAllStripeBilling();
                void loadBillingMetrics();
                void loadAiTokenPurchases();
              }}
              disabled={invoicesSyncing}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-[13px] font-semibold bg-purple-600 text-white rounded-lg active:scale-95 transition-all disabled:opacity-50"
            >
              {invoicesSyncing ? (
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
              ) : (
                <RefreshCw className="h-3.5 w-3.5" />
              )}
              Sync from Stripe
            </button>
          </div>

          {billingMetricsLoading && !billingMetrics ? (
            <div className="flex gap-2 mb-4 overflow-x-auto pb-1">
              {[1, 2, 3, 4, 5].map((i) => (
                <div
                  key={i}
                  className="bg-white rounded-[14px] p-3.5 min-w-[148px] flex-1 h-[88px] animate-pulse"
                  style={{ boxShadow: '0 0 0 0.5px rgba(0,0,0,0.04), 0 1px 2px rgba(0,0,0,0.04)' }}
                />
              ))}
            </div>
          ) : billingMetrics ? (
            <>
              <div className="flex gap-2 mb-2 overflow-x-auto pb-1">
                <InvoiceMetricCard
                  label="MRR"
                  value={formatCad(billingMetrics.mrr_total)}
                  icon={TrendingUp}
                  accentClass="bg-purple-100 text-purple-600"
                />
                <InvoiceMetricCard
                  label="Subscriptions"
                  value={String(
                    billingMetrics.active_subscriptions +
                      billingMetrics.trialing_subscriptions +
                      billingMetrics.past_due_subscriptions,
                  )}
                  sub={
                    billingMetrics.past_due_subscriptions > 0
                      ? `${billingMetrics.active_subscriptions} active · ${billingMetrics.past_due_subscriptions} past due`
                      : `${billingMetrics.active_subscriptions} active · ${billingMetrics.trialing_subscriptions} trialing`
                  }
                  icon={CreditCard}
                  accentClass="bg-green-100 text-green-600"
                />
                <InvoiceMetricCard
                  label="Collected"
                  value={formatCadCents(billingMetrics.collected_this_month)}
                  sub={`${formatCadCents(billingMetrics.collected_all_time)} all time`}
                  icon={DollarSign}
                  accentClass="bg-emerald-100 text-emerald-600"
                />
                <InvoiceMetricCard
                  label="AI tokens"
                  value={formatCadCents(billingMetrics.ai_tokens_this_month ?? 0)}
                  sub={`${formatCadCents(billingMetrics.ai_tokens_all_time ?? 0)} all time · ${billingMetrics.ai_token_purchases_this_month ?? 0} buys`}
                  icon={Zap}
                  accentClass="bg-sky-100 text-sky-600"
                />
                <InvoiceMetricCard
                  label="Outstanding"
                  value={formatCadCents(billingMetrics.outstanding)}
                  sub={`${billingMetrics.open_invoices} open · ${billingMetrics.paid_invoices} paid`}
                  icon={AlertCircle}
                  accentClass="bg-amber-100 text-amber-600"
                />
              </div>
              <BillingByDayCard rates={cadRates} />
              <p className="text-[12px] text-gray-400 mb-4">
                {billingMetrics.total_invoices.toLocaleString()} invoices synced
                {invoicesTotal !== billingMetrics.total_invoices && invoiceStatusFilter !== 'all'
                  ? ` · ${invoicesTotal.toLocaleString()} matching filter`
                  : ''}
                {(billingMetrics.ai_tokens_units_this_month ?? 0) > 0
                  ? ` · ${(billingMetrics.ai_tokens_units_this_month / 1_000_000).toFixed(1)}M tokens bought this month`
                  : ''}
                {' · All amounts in CAD'}
              </p>
            </>
          ) : null}

          <div
            className="bg-white rounded-[14px] overflow-hidden mb-4"
            style={{ boxShadow: '0 0 0 0.5px rgba(0,0,0,0.04), 0 1px 2px rgba(0,0,0,0.04)' }}
          >
            <div className="px-4 py-3 border-b border-gray-100 flex items-center justify-between gap-2">
              <div>
                <p className="text-[13px] font-semibold text-gray-900">AI token purchases</p>
                <p className="text-[12px] text-gray-400">
                  Manual packs and auto-topups (not on Stripe invoices)
                </p>
              </div>
              {aiTokenPurchasesLoading && (
                <Loader2 className="h-4 w-4 animate-spin text-gray-400" />
              )}
            </div>
            {aiTokenPurchasesLoading && aiTokenPurchases.length === 0 ? (
              <div className="px-4 py-8 text-center text-[13px] text-gray-400">Loading…</div>
            ) : aiTokenPurchases.length === 0 ? (
              <div className="px-4 py-8 text-center text-[13px] text-gray-400">
                No paid token purchases yet
              </div>
            ) : (
              aiTokenPurchases.map((row, idx) => (
                <div
                  key={row.id}
                  className={`px-4 py-3 ${idx > 0 ? 'border-t border-gray-100' : ''}`}
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0 flex-1">
                      <p className="text-[15px] font-medium text-gray-900 truncate">
                        {row.business_name || 'Unknown business'}
                      </p>
                      <p className="text-[13px] text-gray-500 mt-0.5">
                        {(row.tokens / 1_000_000).toLocaleString(undefined, {
                          maximumFractionDigits: 1,
                        })}
                        M tokens
                        {' · '}
                        {row.operation_type === 'auto_topup' ? 'Auto-topup' : 'Pack purchase'}
                        {' · '}
                        {new Date(row.created_at).toLocaleDateString('en-US', {
                          month: 'short',
                          day: 'numeric',
                          year: 'numeric',
                        })}
                      </p>
                    </div>
                    <p className="text-[15px] font-semibold text-gray-900 tabular-nums whitespace-nowrap">
                      {formatCadCents(row.amount_cad_cents)}
                    </p>
                  </div>
                </div>
              ))
            )}
          </div>

          <div className="relative mb-3">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
            <input
              type="text"
              placeholder="Search invoice #, email, customer ID..."
              value={invoiceSearch}
              onChange={(e) => {
                setInvoiceSearch(e.target.value);
                setInvoicePage(1);
              }}
              className="w-full pl-9 pr-4 py-2.5 bg-white rounded-[12px] text-[15px] text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-purple-500/30 transition-all"
              style={{ boxShadow: '0 0 0 0.5px rgba(0,0,0,0.04), 0 1px 2px rgba(0,0,0,0.04)', fontSize: '16px' }}
            />
          </div>

          <div className="flex gap-2 mb-4 overflow-x-auto">
            {(['all', 'paid', 'open', 'void', 'uncollectible'] as const).map((status) => (
              <button
                key={status}
                onClick={() => {
                  setInvoiceStatusFilter(status);
                  setInvoicePage(1);
                }}
                className={`px-3 py-1.5 text-[13px] font-semibold rounded-lg whitespace-nowrap transition-all ${
                  invoiceStatusFilter === status
                    ? 'bg-purple-600 text-white'
                    : 'bg-gray-100 text-gray-600'
                }`}
              >
                {status === 'all' ? 'All' : status.charAt(0).toUpperCase() + status.slice(1)}
              </button>
            ))}
          </div>

          {invoicesLoading ? (
            <div className="flex items-center justify-center py-20">
              <div className="animate-spin rounded-full h-8 w-8 border-2 border-purple-500 border-t-transparent"></div>
            </div>
          ) : stripeInvoices.length === 0 ? (
            <div className="bg-white rounded-[14px] p-8 text-center" style={{ boxShadow: '0 0 0 0.5px rgba(0,0,0,0.04), 0 1px 2px rgba(0,0,0,0.04)' }}>
              <Receipt className="h-10 w-10 text-gray-300 mx-auto mb-3" />
              <p className="text-[15px] font-medium text-gray-500 mb-1">No invoices yet</p>
              <p className="text-[13px] text-gray-400 mb-4">Sync from Stripe to import your billing history.</p>
              <button
                onClick={handleSyncAllStripeBilling}
                disabled={invoicesSyncing}
                className="inline-flex items-center gap-2 px-4 py-2 text-[14px] font-semibold bg-purple-600 text-white rounded-lg disabled:opacity-50"
              >
                {invoicesSyncing ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
                Sync from Stripe
              </button>
            </div>
          ) : (
            <>
              <div className="bg-white rounded-[14px] overflow-hidden" style={{ boxShadow: '0 0 0 0.5px rgba(0,0,0,0.04), 0 1px 2px rgba(0,0,0,0.04)' }}>
                {stripeInvoices.map((inv, idx) => (
                  <div key={inv.id} className={`px-4 py-3.5 ${idx > 0 ? 'border-t border-gray-100' : ''}`}>
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <span className={`inline-flex px-2 py-0.5 text-[11px] font-bold rounded-full uppercase tracking-wide ${stripeInvoiceStatusClass(inv.status)}`}>
                            {inv.status}
                          </span>
                          <span className="text-[12px] text-gray-400">
                            {new Date(inv.invoice_created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                          </span>
                        </div>
                        <p className="text-[15px] font-medium text-gray-900 truncate">
                          {inv.businesses?.name || inv.customer_email || 'Unknown customer'}
                        </p>
                        <p className="text-[13px] text-gray-500 mt-0.5">
                          {inv.number ? `#${inv.number}` : inv.stripe_invoice_id}
                          {inv.customer_email && inv.businesses?.name ? ` · ${inv.customer_email}` : ''}
                        </p>
                      </div>
                      <div className="text-right flex-shrink-0">
                        <p className="text-[15px] font-semibold text-gray-900">
                          {formatMirrorInvoiceAmountCad(inv.amount_paid || inv.amount_due, inv.currency, cadRates)}
                        </p>
                        <div className="flex items-center justify-end gap-2 mt-1">
                          {stripeInvoiceAvailableActions(inv.status).length > 0 && (
                            <button
                              type="button"
                              onClick={() => setInvoiceActionTarget(inv)}
                              className="text-[12px] font-semibold text-purple-600 hover:text-purple-700"
                            >
                              Manage
                            </button>
                          )}
                          {(inv.hosted_invoice_url || inv.invoice_pdf) && (
                            <a
                              href={inv.hosted_invoice_url || inv.invoice_pdf || '#'}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="inline-flex items-center gap-1 text-[12px] font-semibold text-purple-600"
                            >
                              View
                              <ExternalLink className="h-3 w-3" />
                            </a>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {invoicesTotal > 50 && (
                <div className="flex items-center justify-between mt-4">
                  <button
                    onClick={() => setInvoicePage((p) => Math.max(1, p - 1))}
                    disabled={invoicePage <= 1}
                    className="px-3 py-1.5 text-[13px] font-semibold bg-gray-100 text-gray-700 rounded-lg disabled:opacity-40"
                  >
                    Previous
                  </button>
                  <span className="text-[13px] text-gray-500">
                    Page {invoicePage} of {Math.ceil(invoicesTotal / 50)}
                  </span>
                  <button
                    onClick={() => setInvoicePage((p) => p + 1)}
                    disabled={invoicePage >= Math.ceil(invoicesTotal / 50)}
                    className="px-3 py-1.5 text-[13px] font-semibold bg-gray-100 text-gray-700 rounded-lg disabled:opacity-40"
                  >
                    Next
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      ) : activeTab === 'partners' ? (
        <ReferralPartnersPanel />
      ) : activeTab === 'signups' ? (
        <div className="px-4 pb-6">
          <SignupAnalyticsPanel />
        </div>
      ) : activeTab === 'signup-flows' ? (
        <div className="px-4 pb-6">
          <SignupFlowReportsPanel />
        </div>
      ) : loading ? (
        <div className="flex items-center justify-center py-20">
          <div className="animate-spin rounded-full h-8 w-8 border-2 border-purple-500 border-t-transparent"></div>
        </div>
      ) : (
        <div className="px-4 pb-6">
              {/* iOS-style search */}
              <div className="relative mb-3">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search businesses..."
                  value={businessSearch}
                  onChange={(e) => setBusinessSearch(e.target.value)}
                  className="w-full pl-9 pr-4 py-2.5 bg-white rounded-[12px] text-[15px] text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-purple-500/30 transition-all"
                  style={{ boxShadow: '0 0 0 0.5px rgba(0,0,0,0.04), 0 1px 2px rgba(0,0,0,0.04)', fontSize: '16px' }}
                />
              </div>

              {/* iOS pill filter */}
              <div className="flex items-center gap-2 mb-4">
                {(['active', 'trialing', 'suspended', 'all'] as const).map(f => (
                  <button
                    key={f}
                    onClick={() => setStatusFilter(f)}
                    className={`px-3.5 py-1.5 text-xs font-semibold rounded-full transition-all active:scale-95 touch-manipulation ${
                      statusFilter === f
                        ? f === 'active' ? 'bg-green-100 text-green-700'
                          : f === 'trialing' ? 'bg-amber-100 text-amber-700'
                          : f === 'suspended' ? 'bg-red-100 text-red-700'
                          : 'bg-purple-100 text-purple-700'
                        : 'bg-white text-gray-500 border border-gray-200/60'
                    }`}
                  >
                    {f === 'all' ? 'All' : f === 'trialing' ? 'Trialling' : f.charAt(0).toUpperCase() + f.slice(1)}
                    <span className={statusFilter === f ? 'ml-1 opacity-70' : 'ml-1 text-gray-400'}>
                      {businessStatusCounts[f]}
                    </span>
                  </button>
                ))}
                <span className="text-xs text-gray-400 ml-auto">{filteredBusinesses.length}</span>
              </div>

              {/* Favorites section */}
              {favoriteBusinesses.length > 0 && (
                <>
                  <div className="flex items-center gap-2 mb-3">
                    <Star className="h-3.5 w-3.5 text-amber-500 fill-amber-500" />
                    <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Favorites</span>
                    <span className="text-xs text-gray-400">{favoriteBusinesses.length}</span>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-5">
                    {favoriteBusinesses.map((business) => renderBusinessRow(business))}
                  </div>
                </>
              )}

              {/* All / Other businesses */}
              {favoriteBusinesses.length > 0 && otherBusinesses.length > 0 && (
                <div className="flex items-center gap-2 mb-3">
                  <Building2 className="h-3.5 w-3.5 text-gray-400" />
                  <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">All Businesses</span>
                  <span className="text-xs text-gray-400">{otherBusinesses.length}</span>
                </div>
              )}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {(favoriteBusinesses.length > 0 ? otherBusinesses : filteredBusinesses).map((business) => renderBusinessRow(business))}
              </div>

              {/* Empty State */}
              {filteredBusinesses.length === 0 && (
                <div className="text-center py-12">
                  <Building2 className="h-8 w-8 text-gray-300 mx-auto mb-3" />
                  <p className="text-sm font-medium text-gray-500">
                    {businesses.length === 0 ? 'No businesses yet' : 'No matching businesses'}
                  </p>
                </div>
              )}
            </div>
      )}

      {/* Delete Business Modal */}
      {showDeleteModal && businessToDelete && (
        <DeleteBusinessModal
          isOpen={showDeleteModal}
          onClose={handleCloseDeleteModal}
          businessId={businessToDelete.id}
          businessName={businessToDelete.name}
          onDeleted={handleBusinessDeleted}
        />
      )}

      {/* Reset Password Modal */}
      {showResetModal && businessToReset && (
        <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center">
          <div className="fixed inset-0 bg-black/40" onClick={handleCloseResetModal}></div>
          <div className="relative bg-white rounded-t-[20px] sm:rounded-[20px] p-6 w-full sm:max-w-md mx-0 sm:mx-4 shadow-xl" style={{ fontFamily: '-apple-system, BlinkMacSystemFont, "SF Pro Display", "SF Pro Text", system-ui, sans-serif' }}>
            <div className="flex items-center space-x-3 mb-5">
              <div className="h-10 w-10 rounded-[12px] bg-blue-100 flex items-center justify-center">
                <Key className="h-5 w-5 text-blue-600" />
              </div>
              <div>
                <h3 className="text-[17px] font-semibold text-gray-900">Set New Password</h3>
                <p className="text-[13px] text-gray-500">Directly set a new password</p>
              </div>
            </div>
            
            <div className="rounded-[12px] p-3 mb-4" style={{ background: 'rgba(118,118,128,0.08)' }}>
              <p className="text-[15px] font-medium text-gray-900">{businessToReset.name}</p>
              <p className="text-[13px] text-gray-500">{businessToReset.email}</p>
            </div>
            
            <div className="rounded-[12px] overflow-hidden mb-5" style={{ background: 'rgba(118,118,128,0.08)' }}>
              <input
                type="password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                className="w-full bg-transparent px-3 py-3 text-[15px] text-gray-900 focus:outline-none border-b border-gray-200/60 placeholder-gray-400"
                placeholder="New password (min 6 characters)"
                disabled={resetLoading}
                style={{ fontSize: '16px' }}
              />
              <input
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                className="w-full bg-transparent px-3 py-3 text-[15px] text-gray-900 focus:outline-none placeholder-gray-400"
                placeholder="Confirm password"
                disabled={resetLoading}
                style={{ fontSize: '16px' }}
              />
            </div>

            {resetMessage && (
              <div className={`mb-4 p-3 rounded-[12px] text-[13px] ${
                resetMessage.type === 'success' 
                  ? 'bg-green-50 text-green-700' 
                  : 'bg-red-50 text-red-700'
              }`}>
                {resetMessage.text}
              </div>
            )}

            <div className="flex gap-3">
              <button
                onClick={handleCloseResetModal}
                className="flex-1 px-4 py-3 text-[15px] font-semibold text-gray-700 bg-gray-100 rounded-[12px] active:scale-[0.98] transition-all touch-manipulation"
                disabled={resetLoading}
                style={{ WebkitTapHighlightColor: 'transparent' }}
              >
                Cancel
              </button>
              <button
                onClick={handleConfirmResetPassword}
                disabled={resetLoading}
                className="flex-1 px-4 py-3 text-[15px] font-semibold bg-blue-600 text-white rounded-[12px] active:scale-[0.98] transition-all touch-manipulation disabled:opacity-50 flex items-center justify-center gap-2"
                style={{ WebkitTapHighlightColor: 'transparent' }}
              >
                {resetLoading ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                    <span>Saving...</span>
                  </>
                ) : (
                  'Set Password'
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Subscription Modal */}
      {showSubscriptionModal && businessForSubscription && (
        <div className="fixed inset-0 bg-black/40 flex items-end sm:items-center justify-center z-50">
          <div className="bg-white rounded-t-[20px] sm:rounded-[20px] shadow-xl w-full sm:max-w-lg mx-0 sm:mx-4 max-h-[85vh] overflow-y-auto" style={{ fontFamily: '-apple-system, BlinkMacSystemFont, "SF Pro Display", "SF Pro Text", system-ui, sans-serif' }}>
            <div className="p-5">
              <div className="flex items-center justify-between mb-5">
                <div className="flex items-center gap-3">
                  <div className="h-10 w-10 rounded-[12px] bg-purple-100 flex items-center justify-center">
                    <CreditCard className="h-5 w-5 text-purple-600" />
                  </div>
                  <div>
                    <h2 className="text-[17px] font-semibold text-gray-900">Subscription</h2>
                    <p className="text-[13px] text-gray-500">{businessForSubscription.name}</p>
                  </div>
                </div>
                <button
                  onClick={handleCloseSubscriptionModal}
                  className="h-8 w-8 flex items-center justify-center rounded-full bg-gray-100 active:scale-95 transition-all touch-manipulation"
                  style={{ WebkitTapHighlightColor: 'transparent' }}
                >
                  <X className="h-4 w-4 text-gray-500" />
                </button>
              </div>

              {subscriptionLoading ? (
                <div className="flex items-center justify-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-2 border-purple-500 border-t-transparent"></div>
                </div>
              ) : subscriptionData ? (
                <div className="space-y-4">
                  {/* Current Plan */}
                  <div className="rounded-[12px] p-4" style={{ background: 'rgba(118,118,128,0.08)' }}>
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-[17px] font-semibold text-gray-900">{subscriptionData.planName || 'No Plan'}</p>
                        <p className="text-[13px] text-gray-500">{subscriptionData.planPrice ? `$${subscriptionData.planPrice}/month` : 'Free'}</p>
                      </div>
                      <span className={`px-2.5 py-1 rounded-full text-[11px] font-bold uppercase tracking-wide ${
                        subscriptionData.status === 'active' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
                      }`}>{subscriptionData.status || 'Unknown'}</span>
                    </div>
                    {(subscriptionData.status !== 'active' || subscriptionData.planName === 'No Plan') && (
                      <button
                        onClick={() => businessForSubscription && handleOpenGrantAccess(businessForSubscription.id, businessForSubscription.name)}
                        className="mt-3 w-full flex items-center justify-center gap-2 px-4 py-2.5 text-[14px] font-semibold bg-green-600 text-white rounded-[10px] active:scale-[0.98] transition-all touch-manipulation"
                        style={{ WebkitTapHighlightColor: 'transparent' }}
                      >
                        <Gift className="h-4 w-4" />
                        Grant Access
                      </button>
                    )}
                  </div>

                  {subscriptionData.includedApps?.length > 0 && (
                    <div>
                      <p className="text-[13px] font-semibold text-gray-500 uppercase tracking-wide mb-2 px-1">Included Apps</p>
                      <div className="rounded-[12px] overflow-hidden" style={{ background: 'rgba(118,118,128,0.08)' }}>
                        {subscriptionData.includedApps.map((app: any, index: number) => (
                          <div key={index} className={`flex items-center gap-2.5 px-3 py-2.5 ${index > 0 ? 'border-t border-gray-200/40' : ''}`}>
                            <CheckCircle className="h-4 w-4 text-green-600 flex-shrink-0" />
                            <span className="text-[15px] text-gray-900">{app.name}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {subscriptionData.addonApps?.length > 0 && (
                    <div>
                      <p className="text-[13px] font-semibold text-gray-500 uppercase tracking-wide mb-2 px-1">Add-ons</p>
                      <div className="rounded-[12px] overflow-hidden" style={{ background: 'rgba(118,118,128,0.08)' }}>
                        {subscriptionData.addonApps.map((app: any, index: number) => (
                          <div key={index} className={`flex items-center justify-between px-3 py-2.5 ${index > 0 ? 'border-t border-gray-200/40' : ''}`}>
                            <div className="flex items-center gap-2.5">
                              <CheckCircle className="h-4 w-4 text-purple-600 flex-shrink-0" />
                              <span className="text-[15px] text-gray-900">{app.name}</span>
                            </div>
                            <span className="text-[13px] text-gray-500">${app.price}/mo</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {subscriptionData.adminGrantedApps?.length > 0 && (
                    <div>
                      <p className="text-[13px] font-semibold text-gray-500 uppercase tracking-wide mb-2 px-1">Admin Granted</p>
                      <div className="rounded-[12px] overflow-hidden" style={{ background: 'rgba(118,118,128,0.08)' }}>
                        {subscriptionData.adminGrantedApps.map((app: any, index: number) => (
                          <div key={index} className={`flex items-center justify-between px-3 py-2.5 ${index > 0 ? 'border-t border-gray-200/40' : ''}`}>
                            <span className="text-[15px] text-gray-900">{app.name}</span>
                            <button
                              onClick={() => handleRemoveAppAccess(app.id, app.name)}
                              className="px-3 py-1 text-[13px] font-semibold text-red-600 bg-red-50 rounded-full active:scale-95 transition-all touch-manipulation"
                              style={{ WebkitTapHighlightColor: 'transparent' }}
                            >
                              Remove
                            </button>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {subscriptionData.availableApps?.filter((a: any) => a.type === 'core').length > 0 && (
                    <div>
                      <p className="text-[13px] font-semibold text-gray-500 uppercase tracking-wide mb-2 px-1">Available Core Apps</p>
                      <div className="rounded-[12px] overflow-hidden" style={{ background: 'rgba(118,118,128,0.08)' }}>
                        {subscriptionData.availableApps.filter((a: any) => a.type === 'core').map((app: any, index: number) => (
                          <div key={index} className={`flex items-center justify-between px-3 py-2.5 ${index > 0 ? 'border-t border-gray-200/40' : ''}`}>
                            <div className="flex items-center gap-2">
                              <span className="text-[15px] text-gray-900">{app.name}</span>
                              {app.inPlan && <span className="text-[10px] font-bold bg-blue-100 text-blue-700 px-1.5 py-0.5 rounded-full">In Plan</span>}
                            </div>
                            <button
                              onClick={() => handleGrantAppAccess(app.id, app.name)}
                              className="px-3 py-1 text-[13px] font-semibold text-green-700 bg-green-50 rounded-full active:scale-95 transition-all touch-manipulation"
                              style={{ WebkitTapHighlightColor: 'transparent' }}
                            >
                              Grant
                            </button>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {subscriptionData.availableApps?.filter((a: any) => a.type === 'addon').length > 0 && (
                    <div>
                      <p className="text-[13px] font-semibold text-gray-500 uppercase tracking-wide mb-2 px-1">Available Add-ons</p>
                      <div className="rounded-[12px] overflow-hidden" style={{ background: 'rgba(118,118,128,0.08)' }}>
                        {subscriptionData.availableApps.filter((a: any) => a.type === 'addon').map((app: any, index: number) => (
                          <div key={index} className={`flex items-center justify-between px-3 py-2.5 ${index > 0 ? 'border-t border-gray-200/40' : ''}`}>
                            <div className="flex items-center gap-2">
                              <span className="text-[15px] text-gray-900">{app.name}</span>
                              <span className="text-[13px] text-gray-400">${app.price}/mo</span>
                            </div>
                            <button
                              onClick={() => handleGrantAppAccess(app.id, app.name)}
                              className="px-3 py-1 text-[13px] font-semibold text-green-700 bg-green-50 rounded-full active:scale-95 transition-all touch-manipulation"
                              style={{ WebkitTapHighlightColor: 'transparent' }}
                            >
                              Grant
                            </button>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  <div>
                    <p className="text-[13px] font-semibold text-gray-500 uppercase tracking-wide mb-2 px-1">Media Connections</p>
                    <div className="rounded-[12px] p-3" style={{ background: 'rgba(118,118,128,0.08)' }}>
                      {socialSeatsLoading ? (
                        <div className="flex items-center justify-center py-3">
                          <Loader2 className="h-4 w-4 animate-spin text-gray-400" />
                        </div>
                      ) : socialSeats ? (
                        <>
                          <div className="flex items-center justify-between mb-3">
                            <div>
                              <p className="text-[15px] font-medium text-gray-900">
                                {socialSeats.active_count} of {socialSeats.total_capacity} connected
                              </p>
                              <p className="text-[12px] text-gray-500">
                                {socialSeats.purchased_quantity} paid · {socialSeats.grandfathered_quantity} free
                              </p>
                            </div>
                            <span className={`px-2 py-0.5 rounded-full text-[11px] font-bold uppercase tracking-wide ${
                              socialSeats.can_add ? 'bg-green-100 text-green-700' : 'bg-gray-200 text-gray-600'
                            }`}>
                              {socialSeats.can_add ? 'Slots open' : 'Full'}
                            </span>
                          </div>
                          <div className="flex items-center justify-between pt-3 border-t border-gray-200/40">
                            <p className="text-[14px] text-gray-900">Free seats (trial)</p>
                            <div className="flex items-center gap-2">
                              <button
                                onClick={() => setFreeSeatsInput(v => Math.max(0, v - 1))}
                                disabled={socialSeatsSaving || freeSeatsInput <= 0}
                                className="h-8 w-8 flex items-center justify-center rounded-full bg-white text-gray-700 text-[18px] font-semibold shadow-sm active:scale-95 transition-all touch-manipulation disabled:opacity-40"
                                style={{ WebkitTapHighlightColor: 'transparent' }}
                              >
                                −
                              </button>
                              <span className="w-8 text-center text-[16px] font-semibold text-gray-900">{freeSeatsInput}</span>
                              <button
                                onClick={() => setFreeSeatsInput(v => Math.min(50, v + 1))}
                                disabled={socialSeatsSaving || freeSeatsInput >= 50}
                                className="h-8 w-8 flex items-center justify-center rounded-full bg-white text-gray-700 text-[18px] font-semibold shadow-sm active:scale-95 transition-all touch-manipulation disabled:opacity-40"
                                style={{ WebkitTapHighlightColor: 'transparent' }}
                              >
                                +
                              </button>
                            </div>
                          </div>
                          {freeSeatsInput !== socialSeats.grandfathered_quantity && (
                            <button
                              onClick={handleSaveFreeSeats}
                              disabled={socialSeatsSaving}
                              className="mt-3 w-full flex items-center justify-center gap-2 px-4 py-2.5 text-[14px] font-semibold bg-purple-600 text-white rounded-[10px] active:scale-[0.98] transition-all touch-manipulation disabled:opacity-60"
                              style={{ WebkitTapHighlightColor: 'transparent' }}
                            >
                              {socialSeatsSaving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Gift className="h-4 w-4" />}
                              {socialSeatsSaving ? 'Saving...' : `Save ${freeSeatsInput} Free Seat${freeSeatsInput === 1 ? '' : 's'}`}
                            </button>
                          )}
                          {socialSeatsMessage && (
                            <p className={`mt-2 text-[13px] font-medium ${socialSeatsMessage.type === 'success' ? 'text-green-600' : 'text-red-600'}`}>
                              {socialSeatsMessage.text}
                            </p>
                          )}
                        </>
                      ) : (
                        <p className="text-[13px] text-gray-500 py-1">Couldn't load media connection seats.</p>
                      )}
                    </div>
                  </div>

                  <div className="pt-2 border-t border-gray-200/60">
                    <div className="flex items-center justify-between mb-2 px-1">
                      <p className="text-[13px] font-semibold text-gray-500 uppercase tracking-wide">Stripe Subscriptions</p>
                      <button
                        onClick={handleSyncBusinessStripeBilling}
                        disabled={stripeBillingSyncing}
                        className="inline-flex items-center gap-1 text-[12px] font-semibold text-purple-600 disabled:opacity-50"
                      >
                        {stripeBillingSyncing ? (
                          <Loader2 className="h-3 w-3 animate-spin" />
                        ) : (
                          <RefreshCw className="h-3 w-3" />
                        )}
                        Sync
                      </button>
                    </div>
                    {stripeBilling?.subscriptions?.length ? (
                      <div className="rounded-[12px] overflow-hidden mb-4" style={{ background: 'rgba(118,118,128,0.08)' }}>
                        {stripeBilling.subscriptions.map((sub, index) => {
                          const timingLines = stripeSubTimingLines(sub);
                          return (
                          <div key={sub.id} className={`px-3 py-2.5 ${index > 0 ? 'border-t border-gray-200/40' : ''}`}>
                            <div className="flex items-center justify-between gap-2">
                              <div className="min-w-0">
                                <p className="text-[15px] font-medium text-gray-900 truncate">
                                  {sub.plan_label || stripeSubscriptionKindLabel(sub.kind)}
                                </p>
                                <p className="text-[12px] text-gray-500">
                                  {sub.amount_monthly != null
                                    ? formatStripeSubscriptionAmount(sub)
                                    : '—'}
                                  {timingLines.length > 0 ? ` · ${timingLines.join(' · ')}` : ''}
                                </p>
                              </div>
                              <span className={`px-2 py-0.5 rounded-full text-[11px] font-bold uppercase tracking-wide ${stripeSubscriptionStatusClass(sub.status)}`}>
                                {sub.status}
                              </span>
                            </div>
                          </div>
                          );
                        })}
                      </div>
                    ) : (
                      <p className="text-[13px] text-gray-400 px-1 mb-4">No Stripe subscriptions synced yet.</p>
                    )}

                    <p className="text-[13px] font-semibold text-gray-500 uppercase tracking-wide mb-2 px-1">Stripe Invoices</p>
                    {stripeBilling?.invoices?.length ? (
                      <div className="rounded-[12px] overflow-hidden" style={{ background: 'rgba(118,118,128,0.08)' }}>
                        {stripeBilling.invoices.slice(0, 20).map((inv, index) => (
                          <div key={inv.id} className={`px-3 py-2.5 ${index > 0 ? 'border-t border-gray-200/40' : ''}`}>
                            <div className="flex items-center justify-between gap-2">
                              <div className="min-w-0">
                                <p className="text-[14px] font-medium text-gray-900 truncate">
                                  {inv.number ? `#${inv.number}` : inv.stripe_invoice_id.slice(0, 16)}
                                </p>
                                <p className="text-[12px] text-gray-500">
                                  {new Date(inv.invoice_created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                                </p>
                              </div>
                              <div className="text-right flex-shrink-0">
                                <p className="text-[14px] font-semibold text-gray-900">
                                  {formatMirrorInvoiceAmountCad(inv.amount_paid || inv.amount_due, inv.currency, cadRates)}
                                </p>
                                <div className="flex items-center justify-end gap-2 mt-0.5">
                                  <span className={`inline-flex px-1.5 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wide ${stripeInvoiceStatusClass(inv.status)}`}>
                                    {inv.status}
                                  </span>
                                  {(inv.hosted_invoice_url || inv.invoice_pdf) && (
                                    <a
                                      href={inv.hosted_invoice_url || inv.invoice_pdf || '#'}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      className="text-[11px] font-semibold text-purple-600"
                                    >
                                      PDF
                                    </a>
                                  )}
                                </div>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-[13px] text-gray-400 px-1">No Stripe invoices synced yet. Tap Sync to pull from Stripe.</p>
                    )}
                  </div>
                </div>
              ) : (
                <div className="text-center py-8">
                  <p className="text-[15px] text-gray-500">No subscription data available</p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      <StripeInvoiceActionModal
        key={invoiceActionTarget?.id || 'closed'}
        isOpen={!!invoiceActionTarget}
        invoice={invoiceActionTarget}
        cadRates={cadRates}
        onClose={() => !invoiceActionLoading && setInvoiceActionTarget(null)}
        onConfirm={handleStripeInvoiceAction}
        isLoading={invoiceActionLoading}
      />

      <GrantBusinessAccessModal
        isOpen={showGrantAccessModal}
        onClose={handleCloseGrantAccessModal}
        onConfirm={handleConfirmGrantBusinessAccess}
        businessName={businessForGrantAccess?.name || ''}
        plans={grantAccessPlans}
        plansLoading={grantAccessPlansLoading}
        isLoading={grantAccessLoading}
      />

      {serviceHoursBusiness && (
        <div className="fixed inset-0 bg-black/40 flex items-end sm:items-center justify-center z-50 p-0 sm:p-4">
          <div
            className="bg-gray-50 rounded-t-[20px] sm:rounded-[20px] w-full sm:max-w-3xl max-h-[92vh] overflow-y-auto p-4 sm:p-5"
            style={{
              fontFamily:
                '-apple-system, BlinkMacSystemFont, "SF Pro Display", "SF Pro Text", system-ui, sans-serif',
            }}
          >
            <SuperAdminServiceHoursPanel
              lockedBusiness={serviceHoursBusiness}
              onClose={() => setServiceHoursBusiness(null)}
            />
          </div>
        </div>
      )}

      {/* Grant App Access Confirmation Modal */}
      {showGrantModal && (
        <div className="fixed inset-0 bg-black/40 flex items-end sm:items-center justify-center z-50">
          <div className="bg-white rounded-t-[20px] sm:rounded-[20px] p-6 w-full sm:max-w-md mx-0 sm:mx-4" style={{ fontFamily: '-apple-system, BlinkMacSystemFont, "SF Pro Display", "SF Pro Text", system-ui, sans-serif' }}>
            <h3 className="text-[17px] font-semibold text-gray-900 mb-2">Grant App Access</h3>
            <p className="text-[15px] text-gray-600 mb-1">
              Grant <span className="font-semibold">{appToGrant?.name}</span> to <span className="font-semibold">{businessForSubscription?.name}</span>?
            </p>
            <p className="text-[13px] text-gray-400 mb-5">Immediate access outside their plan.</p>

            {grantMessage && (
              <div className={`mb-4 p-3 rounded-[12px] text-[13px] ${grantMessage.type === 'success' ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'}`}>{grantMessage.text}</div>
            )}

            <div className="flex gap-3">
              <button onClick={handleCloseGrantModal} disabled={grantLoading} className="flex-1 px-4 py-3 text-[15px] font-semibold text-gray-700 bg-gray-100 rounded-[12px] active:scale-[0.98] transition-all touch-manipulation" style={{ WebkitTapHighlightColor: 'transparent' }}>Cancel</button>
              <button onClick={handleConfirmGrantAccess} disabled={grantLoading} className="flex-1 px-4 py-3 text-[15px] font-semibold bg-green-600 text-white rounded-[12px] active:scale-[0.98] transition-all touch-manipulation disabled:opacity-50 flex items-center justify-center gap-2" style={{ WebkitTapHighlightColor: 'transparent' }}>
                {grantLoading ? <><div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></div><span>Granting...</span></> : 'Grant Access'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Business Users Modal (for Remote Access) */}
      {showUsersForBusiness && (
        <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center">
          <div className="fixed inset-0 bg-black/40" onClick={() => setShowUsersForBusiness(null)}></div>
          <div className="relative bg-white rounded-t-[20px] sm:rounded-[20px] p-6 w-full sm:max-w-lg mx-0 sm:mx-4 shadow-xl max-h-[80vh] overflow-y-auto" style={{ fontFamily: '-apple-system, BlinkMacSystemFont, "SF Pro Display", "SF Pro Text", system-ui, sans-serif' }}>
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-3">
                <div className="h-10 w-10 rounded-[12px] bg-indigo-100 flex items-center justify-center">
                  <Monitor className="h-5 w-5 text-indigo-600" />
                </div>
                <div>
                  <h3 className="text-[17px] font-semibold text-gray-900">Remote Access</h3>
                  <p className="text-[13px] text-gray-500">{showUsersForBusiness.name}</p>
                </div>
              </div>
              <button
                onClick={() => setShowUsersForBusiness(null)}
                className="h-8 w-8 flex items-center justify-center rounded-full bg-gray-100 active:scale-95 transition-all touch-manipulation"
                style={{ WebkitTapHighlightColor: 'transparent' }}
              >
                <X className="h-4 w-4 text-gray-500" />
              </button>
            </div>
            <p className="text-[13px] text-gray-500 mb-4">
              Log directly into any account, or request remote screen access (they must approve).
            </p>
            {remoteAccessLoading ? (
              <div className="flex items-center justify-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-2 border-indigo-500 border-t-transparent"></div>
              </div>
            ) : (
              <BusinessUserList
                businessId={showUsersForBusiness.id}
                businessName={showUsersForBusiness.name}
                onRequestRemoteAccess={handleRequestRemoteAccess}
                onLoginAs={handleLoginAsUser}
                loginAsUserId={impersonatingUserId}
              />
            )}
          </div>
        </div>
      )}

      {/* Remote Access Viewer */}
      {activeRemoteSession && (
        <RemoteAccessViewer
          session={activeRemoteSession}
          onClose={() => setActiveRemoteSession(null)}
        />
      )}

      {/* Remove Access Confirmation Modal */}
      {showRemoveModal && (
        <div className="fixed inset-0 bg-black/40 flex items-end sm:items-center justify-center z-50">
          <div className="bg-white rounded-t-[20px] sm:rounded-[20px] p-6 w-full sm:max-w-md mx-0 sm:mx-4" style={{ fontFamily: '-apple-system, BlinkMacSystemFont, "SF Pro Display", "SF Pro Text", system-ui, sans-serif' }}>
            <h3 className="text-[17px] font-semibold text-gray-900 mb-2">Remove App Access</h3>
            <p className="text-[15px] text-gray-600 mb-1">
              Remove <span className="font-semibold">{appToRemove?.name}</span> from <span className="font-semibold">{businessForSubscription?.name}</span>?
            </p>
            <p className="text-[13px] text-gray-400 mb-5">Access will be revoked immediately.</p>

            {removeMessage && (
              <div className={`mb-4 p-3 rounded-[12px] text-[13px] ${removeMessage.type === 'success' ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'}`}>{removeMessage.text}</div>
            )}

            <div className="flex gap-3">
              <button onClick={handleCloseRemoveModal} disabled={removeLoading} className="flex-1 px-4 py-3 text-[15px] font-semibold text-gray-700 bg-gray-100 rounded-[12px] active:scale-[0.98] transition-all touch-manipulation" style={{ WebkitTapHighlightColor: 'transparent' }}>Cancel</button>
              <button onClick={handleConfirmRemoveAccess} disabled={removeLoading} className="flex-1 px-4 py-3 text-[15px] font-semibold bg-red-600 text-white rounded-[12px] active:scale-[0.98] transition-all touch-manipulation disabled:opacity-50 flex items-center justify-center gap-2" style={{ WebkitTapHighlightColor: 'transparent' }}>
                {removeLoading ? <><div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></div><span>Removing...</span></> : 'Remove Access'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Portal Panel */}
      <PortalPanel
        isOpen={!!portalBusiness}
        onClose={() => setPortalBusiness(null)}
        businessId={portalBusiness?.id || ''}
        businessName={portalBusiness?.name || ''}
        isSuperAdmin={true}
      />
    </div>
  );
}

export default SuperAdminDashboard;